import React, { useState, useEffect, useRef } from 'react';
import {
  ArrowLeft, Sparkles, Upload, X, Download, Loader2, Wand2, Film, Clapperboard,
  Coins, Plus, Play, RefreshCw, Layers, MessageSquarePlus, Video, Music2, ImagePlus,
  Pencil, Check, ChevronDown, Sparkle, Lock, SlidersHorizontal, MessageSquare, Paperclip, Zap,
  BarChart3,
} from 'lucide-react';
import axios from 'axios';
import { API_URL } from '../../config';
import VideoTrimModal from './VideoTrimModal';
import AudioTrimModal from './AudioTrimModal';
import StripePaymentForm from './StripePaymentForm';
import MyConsumptionStatsModal from './MyConsumptionStatsModal';

function formatAr(n) { return `${(n || 0).toLocaleString('fr-FR')} Ar`; }

const MVOLA_NUMBER = '034 26 126 52';
const MVOLA_ACCOUNT_NAME = 'TDI DIGITAL';
const ORANGE_NUMBER = '037 34 130 06';
const ORANGE_ACCOUNT_NAME = 'HERY NY AINA MIADANTSOA';

// Mode "Champs detailles" (alternative au prompt libre) : categories de base reprises de la
// bibliotheque de templates CM Studio et de TeckiA Image Studio (Environnement/Ambiance/Tenue/
// Mouvement/Action), etendues avec Personnage (visage/acteur) et Info complementaire.
const STRUCTURED_FIELDS = [
  { key: 'environment', label: 'Environnement', placeholder: 'Ex : un salon moderne baigné de lumière naturelle...' },
  { key: 'ambiance', label: 'Ambiance', placeholder: 'Ex : chaleureuse, premium, lumière dorée du soir...' },
  { key: 'character', label: 'Personnage', placeholder: 'Ex : le visage de @image1, jeune femme malgache souriante...' },
  { key: 'outfit', label: 'Tenue', placeholder: 'Ex : la tenue de @image2...' },
  { key: 'movement', label: 'Mouvement', placeholder: 'Ex : légère rotation de la caméra...' },
  { key: 'action', label: 'Action', placeholder: 'Ex : tient le produit @image1 et sourit...' },
  { key: 'info', label: 'Info complémentaire', placeholder: 'Ex : tout autre détail important pour cette génération...' },
];

// veo_lite/veo_fast/veo_quality partagent une config strictement identique cote backend
// (seuls label + prix changent, cf VIDEO_PROVIDERS dans ia_studio_routes.py) : on les regroupe
// en une seule tuile "TeckiA V" dans le picker, le choix Lite/Fast/Quality se faisant ensuite
// via un selecteur de qualite dans la zone options (a cote resolution/duree).
const VEO_TIERS = [
  { key: 'veo_lite', short: 'Lite' },
  { key: 'veo_fast', short: 'Fast' },
  { key: 'veo_quality', short: 'Quality' },
];
const isVeoTierKey = (key) => VEO_TIERS.some(t => t.key === key);
const VEO_GROUP_INFO = "Gamme TeckiA V (Google Veo) : jusqu'à la 4K, 8 secondes, image vers vidéo. "
  + "Choisissez ensuite la qualité (Lite/Fast/Quality) juste en dessous.";

// Identite visuelle par provider (tuile "boite 3D" compacte du picker Style) : chaque provider
// a sa propre couleur + icone pour etre reconnaissable d'un coup d'oeil, meme principe que les
// ServiceCard de ClientDashboard.jsx (degrade unique par module) mais adapte a une grille dense.
const PROVIDER_THEME = {
  grok:           { icon: Wand2,         color: 'from-sky-500 to-blue-600',      glow: 'shadow-[0_0_18px_rgba(56,189,248,0.5)]' },
  omni:           { icon: MessageSquare, color: 'from-fuchsia-500 to-pink-600',  glow: 'shadow-[0_0_18px_rgba(217,70,239,0.5)]' },
  seedance_mini:  { icon: Zap,           color: 'from-emerald-500 to-teal-600',  glow: 'shadow-[0_0_18px_rgba(16,185,129,0.5)]' },
  veo_group:      { icon: Sparkles,      color: 'from-amber-500 to-orange-600',  glow: 'shadow-[0_0_18px_rgba(245,158,11,0.5)]' },
  infinitalk:     { icon: Music2,        color: 'from-rose-500 to-red-600',      glow: 'shadow-[0_0_18px_rgba(244,63,94,0.5)]' },
  motion_control: { icon: Clapperboard,  color: 'from-indigo-500 to-violet-600', glow: 'shadow-[0_0_18px_rgba(99,102,241,0.5)]' },
};
const providerTheme = (key) => PROVIDER_THEME[key] || PROVIDER_THEME.grok;

function TeckiALoader({ subtitle }) {
  return (
    <div className="flex flex-col items-center justify-center gap-5 py-12">
      <div className="relative w-36 h-36 flex items-center justify-center">
        <div className="ia-loader-radar absolute w-24 h-24 rounded-full border-2 border-purple-400" />
        <div className="ia-loader-ring absolute w-32 h-32">
          {[0, 90, 180, 270].map(deg => (
            <div
              key={deg}
              className="absolute w-2.5 h-2.5 rounded-full bg-purple-300"
              style={{
                boxShadow: '0 0 8px 2px rgba(139,92,246,0.8)',
                top: deg === 0 ? 0 : deg === 180 ? 'auto' : '50%',
                bottom: deg === 180 ? 0 : 'auto',
                left: deg === 270 ? 0 : deg === 90 ? 'auto' : '50%',
                right: deg === 90 ? 0 : 'auto',
                transform: (deg === 0 || deg === 180) ? 'translateX(-50%)' : (deg === 90 || deg === 270) ? 'translateY(-50%)' : undefined,
              }}
            />
          ))}
        </div>
        <div className="ia-loader-orb w-[76px] h-[76px] rounded-3xl overflow-hidden shadow-[0_0_24px_rgba(139,92,246,0.6)]">
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-300 via-purple-500 to-indigo-600">
            <Film className="w-7 h-7 text-white" />
          </div>
        </div>
      </div>
      <div className="text-center">
        <div className="text-white font-bold text-sm">TeckiA génère votre vidéo...</div>
        <div className="text-gray-400 text-xs mt-1">{subtitle}</div>
      </div>
    </div>
  );
}

function Retention14DaysNotice() {
  return (
    <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-2xl p-4 flex items-start gap-3">
      <Sparkles className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
      <div>
        <p className="text-amber-300 font-bold text-sm mb-0.5">💡 Astuce</p>
        <p className="text-gray-300 text-xs">Les vidéos générées sont disponibles 14 jours. Téléchargez-les rapidement pour ne pas les perdre !</p>
      </div>
    </div>
  );
}

function TopupModal({ onClose, onSubmit, submitting, serviceId, onCardSuccess }) {
  // Etat local (pas remonte au parent) : le parent IAStudioVideoPanel a des dizaines de
  // useState (storyboard, generation...) et re-rendait tout l'ecran a chaque frappe dans ce
  // champ quand amount/method/ref_ vivaient la-bas - latence visible signalee par Hery le
  // 2026-08-20. onSubmit recoit desormais les valeurs directement, comme deja fait cote mobile.
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('mvola');
  const [ref_, setRef] = useState('');
  // Depuis la refonte tarifaire du 2026-08-24 : la carte n'accepte plus un montant libre, le
  // client choisit un pack (bonus credite en plus du prix paye). MVola et Orange restent en
  // montant libre, credites 1:1 sans bonus (declaration manuelle + validation admin).
  //
  // MVola repasse en manuel le 2026-08-28 : meme avec les headers exacts recommandes par le
  // support MVola (X-CorrelationID timestamp, UserLanguage MG, sans UserAccountIdentifier ni
  // partnerName en header), la meme erreur "Missing field"/formatError/mmErrorCode 4001 persiste
  // - probablement une config du compte marchand plutot qu'un souci de requete. Mail de suivi
  // envoye avec date/heure exacte + X-CorrelationID + reponse complete, comme demande par MVola.
  const [packs, setPacks] = useState([]);
  const [loadingPacks, setLoadingPacks] = useState(false);
  const [selectedPackId, setSelectedPackId] = useState(null);

  useEffect(() => {
    if (method !== 'card' || packs.length || loadingPacks) return;
    setLoadingPacks(true);
    axios.get(`${API_URL}/api/ia-studio/credit-packs`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
    }).then(res => {
      if (res.data.success) setPacks(res.data.packs);
    }).finally(() => setLoadingPacks(false));
  }, [method]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedPack = packs.find(p => p.pack_id === selectedPackId) || null;

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div
        className={`bg-[#140a24] border border-purple-500/25 rounded-2xl w-full p-5 overflow-hidden transition-all ${method === 'card' && selectedPack ? 'max-w-md' : 'max-w-sm'}`}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-bold text-base">Recharger mes crédits vidéo</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center">
            <X className="w-4 h-4 text-white" />
          </button>
        </div>
        <label className="block text-white text-xs font-semibold mb-1.5">Méthode de paiement</label>
        <div className="flex gap-2 mb-3">
          {[{ id: 'mvola', label: 'MVola' }, { id: 'orange', label: 'Orange Money' }, { id: 'card', label: 'Carte bancaire' }].map(m => (
            <button
              key={m.id} onClick={() => setMethod(m.id)}
              className={`flex-1 py-2 rounded-xl text-xs font-bold border transition ${method === m.id ? 'bg-purple-600 border-purple-400 text-white' : 'bg-white/5 border-white/10 text-gray-400'}`}
            >
              {m.label}
            </button>
          ))}
        </div>

        {method === 'card' ? (
          <>
            <label className="block text-white text-xs font-semibold mb-1.5">Choisissez un pack</label>
            {loadingPacks ? (
              <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 text-purple-400 animate-spin" /></div>
            ) : (
              <div className="grid grid-cols-2 gap-2 mb-3">
                {packs.map(p => (
                  <button
                    key={p.pack_id}
                    onClick={() => setSelectedPackId(p.pack_id)}
                    className={`text-left p-2.5 rounded-xl border transition ${selectedPackId === p.pack_id ? 'bg-purple-600/30 border-purple-400' : 'bg-white/5 border-white/10 hover:border-white/20'}`}
                  >
                    <div className="text-white text-sm font-bold">{formatAr(p.price_ar)}</div>
                    <div className="text-purple-300 text-[11px] font-semibold">→ {formatAr(p.credited_ar)}</div>
                    {p.bonus_percent > 0 && (
                      <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold text-amber-300">
                        <Sparkles className="w-3 h-3" /> +{p.bonus_percent}%
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
            {selectedPack ? (
              <StripePaymentForm
                key={selectedPack.pack_id}
                cart={[{ service: serviceId, pack_id: selectedPack.pack_id, quantity: 1 }]}
                getTotalAmount={() => selectedPack.price_ar}
                userId={localStorage.getItem('user_id')}
                onSuccess={onCardSuccess}
                onCancel={onClose}
              />
            ) : (
              !loadingPacks && (
                <p className="text-gray-500 text-[11px] mb-1">Sélectionnez un pack pour continuer.</p>
              )
            )}
          </>
        ) : (
          <>
            <label className="block text-white text-xs font-semibold mb-1.5">Montant (Ariary)</label>
            <input
              type="number" value={amount} onChange={e => setAmount(e.target.value)}
              placeholder="Ex : 20000"
              className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-purple-400 mb-3"
            />
            <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl p-3 text-[11px] text-purple-100 mb-3">
              Envoyez votre paiement au numéro <span className="font-semibold">{method === 'mvola' ? MVOLA_NUMBER : ORANGE_NUMBER}</span>{' '}
              au nom de <span className="font-semibold">{method === 'mvola' ? MVOLA_ACCOUNT_NAME : ORANGE_ACCOUNT_NAME}</span>, puis indiquez la référence reçue par SMS ci-dessous.
            </div>
            <label className="block text-white text-xs font-semibold mb-1.5">Référence de paiement</label>
            <input
              value={ref_} onChange={e => setRef(e.target.value)}
              placeholder="Numéro de transaction..."
              className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-purple-400 mb-4"
            />
            <p className="text-gray-500 text-[11px] mb-4">
              Effectuez le paiement puis déclarez-le ici. Votre solde sera crédité après vérification.
            </p>
            <button
              onClick={() => onSubmit(amount, method, ref_)}
              disabled={!amount || !ref_.trim() || submitting}
              className="w-full py-3 rounded-xl font-bold text-white flex items-center justify-center gap-2 disabled:opacity-40 transition"
              style={{ background: 'linear-gradient(135deg, #9333EA, #DB2777)' }}
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Coins className="w-4 h-4" />}
              Déclarer le paiement
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default function IAStudioVideoPanel({ onBack, seed, onSeedConsumed }) {
  const token = localStorage.getItem('auth_token');
  const authHeaders = { Authorization: `Bearer ${token}` };

  const [mode, setMode] = useState('normal'); // 'normal' | 'storyboard'
  const [providers, setProviders] = useState({});
  const [credits, setCredits] = useState(0);
  const [showTopup, setShowTopup] = useState(false);
  const [showMyStats, setShowMyStats] = useState(false);
  const [trimModal, setTrimModal] = useState(null); // { file, maxDuration, onConfirm, onCancel }
  const [audioTrimModal, setAudioTrimModal] = useState(null); // { file, maxDuration, onConfirm, onCancel }
  const [submittingTopup, setSubmittingTopup] = useState(false);

  // ── Mode normal ──
  const [provider, setProvider] = useState('');
  const [promptMode, setPromptMode] = useState('free'); // 'free' | 'structured'
  const [brief, setBrief] = useState('');
  const [generatingPrompt, setGeneratingPrompt] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [fieldEnvironment, setFieldEnvironment] = useState('');
  const [fieldAmbiance, setFieldAmbiance] = useState('');
  const [fieldCharacter, setFieldCharacter] = useState('');
  const [fieldOutfit, setFieldOutfit] = useState('');
  const [fieldMovement, setFieldMovement] = useState('');
  const [fieldAction, setFieldAction] = useState('');
  const [fieldInfo, setFieldInfo] = useState('');
  const [structuredFocus, setStructuredFocus] = useState('action');
  const [resolution, setResolution] = useState('');
  const [aspectRatio, setAspectRatio] = useState('');
  const [duration, setDuration] = useState(0);
  const [refFiles, setRefFiles] = useState([]);
  const [refPreviews, setRefPreviews] = useState([]);
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [refVideoFiles, setRefVideoFiles] = useState([]);
  const [refAudioFiles, setRefAudioFiles] = useState([]);
  const [firstFrameFile, setFirstFrameFile] = useState(null);
  const [firstFramePreview, setFirstFramePreview] = useState(null);
  const [lastFrameFile, setLastFrameFile] = useState(null);
  const [lastFramePreview, setLastFramePreview] = useState(null);
  const [generateAudio, setGenerateAudio] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [currentJob, setCurrentJob] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const fileInputRef = useRef(null);
  const fieldFileInputRef = useRef(null);
  const [pendingFieldKey, setPendingFieldKey] = useState(null);
  const videoInputRef = useRef(null);
  const refVideoInputRef = useRef(null);
  const refAudioInputRef = useRef(null);
  const firstFrameInputRef = useRef(null);
  const lastFrameInputRef = useRef(null);
  const pollRef = useRef(null);
  const pollTimeoutRef = useRef(null);

  // ── Mode storyboard ──
  const [storyboards, setStoryboards] = useState([]);
  const [loadingStoryboards, setLoadingStoryboards] = useState(false);
  const [activeStoryboard, setActiveStoryboard] = useState(null);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [savingTitle, setSavingTitle] = useState(false);
  const [sceneProvider, setSceneProvider] = useState('');
  const [scenePromptMode, setScenePromptMode] = useState('free'); // 'free' | 'structured'
  const [sceneBrief, setSceneBrief] = useState('');
  const [generatingScenePrompt, setGeneratingScenePrompt] = useState(false);
  const [scenePrompt, setScenePrompt] = useState('');
  const [sceneFieldEnvironment, setSceneFieldEnvironment] = useState('');
  const [sceneFieldAmbiance, setSceneFieldAmbiance] = useState('');
  const [sceneFieldCharacter, setSceneFieldCharacter] = useState('');
  const [sceneFieldOutfit, setSceneFieldOutfit] = useState('');
  const [sceneFieldMovement, setSceneFieldMovement] = useState('');
  const [sceneFieldAction, setSceneFieldAction] = useState('');
  const [sceneFieldInfo, setSceneFieldInfo] = useState('');
  const [sceneStructuredFocus, setSceneStructuredFocus] = useState('action');
  const [sceneResolution, setSceneResolution] = useState('');
  const [sceneAspectRatio, setSceneAspectRatio] = useState('');
  const [sceneDuration, setSceneDuration] = useState(0);
  const [sceneRefFiles, setSceneRefFiles] = useState([]);
  const [sceneRefPreviews, setSceneRefPreviews] = useState([]);
  const [sceneVideoFile, setSceneVideoFile] = useState(null);
  const [sceneVideoPreview, setSceneVideoPreview] = useState(null);
  const [sceneRefVideoFiles, setSceneRefVideoFiles] = useState([]);
  const [sceneRefAudioFiles, setSceneRefAudioFiles] = useState([]);
  const [sceneFirstFrameFile, setSceneFirstFrameFile] = useState(null);
  const [sceneFirstFramePreview, setSceneFirstFramePreview] = useState(null);
  const [sceneLastFrameFile, setSceneLastFrameFile] = useState(null);
  const [sceneLastFramePreview, setSceneLastFramePreview] = useState(null);
  const [sceneGenerateAudio, setSceneGenerateAudio] = useState(false);
  // Continuite explicite (case a cocher) plutot qu'un comportement implicite : le backend
  // combine desormais la derniere frame avec d'eventuelles autres photos de reference au lieu
  // de laisser l'une ecraser l'autre (demande explicite de Hery, 2026-08-19).
  const [sceneUseLastFrame, setSceneUseLastFrame] = useState(true);
  const [addingScene, setAddingScene] = useState(false);
  // Non-null = le composeur "Nouvelle scene" est en fait en train de MODIFIER/regenerer cette
  // scene existante (completed/failed) plutot que d'en ajouter une nouvelle - meme formulaire
  // reutilise, juste une route et un libelle differents (demande explicite de Hery, 2026-08-19).
  const [editingSceneId, setEditingSceneId] = useState(null);
  const [assembling, setAssembling] = useState(false);
  const scenePollRef = useRef(null);
  const sceneFileInputRef = useRef(null);
  const sceneFieldFileInputRef = useRef(null);
  const [pendingSceneFieldKey, setPendingSceneFieldKey] = useState(null);
  const sceneVideoInputRef = useRef(null);
  const sceneRefVideoInputRef = useRef(null);
  const sceneRefAudioInputRef = useRef(null);
  const sceneFirstFrameInputRef = useRef(null);
  const sceneLastFrameInputRef = useRef(null);

  useEffect(() => {
    loadProviders();
    loadCredits();
    loadHistory();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
      if (scenePollRef.current) clearInterval(scenePollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (mode === 'storyboard' && storyboards.length === 0) loadStoryboards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Arrivee depuis "Utiliser en vidéo" (Image Studio, voir ClientDashboard) : on recupere
  // l'image deja generee (URL publique, pas besoin de re-upload manuel) et on la depose comme
  // photo de reference dans le mode choisi (normal/storyboard), une seule fois (onSeedConsumed
  // vide le seed cote parent pour ne pas re-ajouter la reference a chaque re-render).
  useEffect(() => {
    if (!seed?.imageUrl) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(seed.imageUrl);
        const blob = await resp.blob();
        const file = new File([blob], 'reference.png', { type: blob.type || 'image/png' });
        if (cancelled) return;
        const reader = new FileReader();
        reader.onload = e => {
          if (seed.mode === 'storyboard') setSceneRefPreviews(prev => [...prev, e.target.result]);
          else setRefPreviews(prev => [...prev, e.target.result]);
        };
        reader.readAsDataURL(file);
        if (seed.mode === 'storyboard') {
          setMode('storyboard');
          setSceneRefFiles(prev => [...prev, file]);
        } else {
          setMode('normal');
          setRefFiles(prev => [...prev, file]);
        }
      } catch (e) { /* silencieux */ }
      onSeedConsumed?.();
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seed]);

  const loadProviders = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/ia-studio/video/providers`, { headers: authHeaders });
      if (res.data.success) {
        setProviders(res.data.providers);
        const first = Object.keys(res.data.providers)[0];
        if (first) {
          const cfg = res.data.providers[first];
          setProvider(first); setResolution(cfg.resolutions[0]); setDuration(cfg.max_duration);
          setAspectRatio(cfg.aspect_ratios[0]);
          setSceneProvider(first); setSceneResolution(cfg.resolutions[0]); setSceneDuration(cfg.max_duration);
          setSceneAspectRatio(cfg.aspect_ratios[0]);
        }
      }
    } catch (e) { /* silencieux */ }
  };

  const loadCredits = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/ia-studio/video/credits`, { headers: authHeaders });
      if (res.data.success) setCredits(res.data.balance);
    } catch (e) { /* silencieux */ }
  };

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await axios.get(`${API_URL}/api/ia-studio/video/history`, { headers: authHeaders });
      if (res.data.success) setHistory(res.data.items);
    } catch (e) { /* silencieux */ }
    setLoadingHistory(false);
  };

  const loadStoryboards = async () => {
    setLoadingStoryboards(true);
    try {
      const res = await axios.get(`${API_URL}/api/ia-studio/storyboard/list`, { headers: authHeaders });
      if (res.data.success) setStoryboards(res.data.items);
    } catch (e) { /* silencieux */ }
    setLoadingStoryboards(false);
  };

  const openStoryboard = async (id) => {
    try {
      const res = await axios.get(`${API_URL}/api/ia-studio/storyboard/${id}`, { headers: authHeaders });
      if (res.data.success) setActiveStoryboard(res.data);
    } catch (e) { /* silencieux */ }
  };

  const createStoryboard = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/ia-studio/storyboard/create`, { title: 'Nouveau storyboard' }, { headers: authHeaders });
      if (res.data.success) {
        await loadStoryboards();
        openStoryboard(res.data.storyboard_id);
      }
    } catch (e) {
      window.alert(e?.response?.data?.message || e?.response?.data?.error || 'Erreur lors de la création');
    }
  };

  const startEditTitle = () => {
    setTitleDraft(activeStoryboard?.title || '');
    setEditingTitle(true);
  };

  const saveTitle = async () => {
    if (!titleDraft.trim() || !activeStoryboard || savingTitle) return;
    setSavingTitle(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/ia-studio/storyboard/${activeStoryboard.storyboard_id}/rename`,
        { title: titleDraft.trim() }, { headers: authHeaders },
      );
      if (res.data.success) {
        setActiveStoryboard(prev => ({ ...prev, title: res.data.title }));
        setStoryboards(prev => prev.map(sb => sb.storyboard_id === activeStoryboard.storyboard_id ? { ...sb, title: res.data.title } : sb));
        setEditingTitle(false);
      }
    } catch (e) { /* silencieux */ }
    setSavingTitle(false);
  };

  // Changer de moteur ne doit PAS reinitialiser ce que le client a deja configure (retour Hery
  // 2026-09-16 : il avait choisi 9:16 sur Omni, clique sur SM pour explorer, et le format
  // repartait silencieusement sur le defaut du nouveau provider - la generation partait donc
  // avec le mauvais format sans que rien ne le signale). On ne retombe sur le defaut du provider
  // QUE si le reglage actuel n'existe pas dans sa liste (resolution/aspect_ratio) ou le depasse
  // (duree) ; les references (video/audio/frames) ne sont videees que si le nouveau provider ne
  // les supporte pas du tout - sinon elles restent utilisables telles quelles.
  const onProviderChange = (p) => {
    setProvider(p);
    const cfg = providers[p];
    if (!cfg) return;
    setResolution(r => cfg.resolutions.includes(r) ? r : cfg.resolutions[0]);
    setAspectRatio(ar => cfg.aspect_ratios.includes(ar) ? ar : cfg.aspect_ratios[0]);
    setDuration(d => Math.min(d, cfg.max_duration));
    if (!cfg.supports_video_ref) { setVideoFile(null); setVideoPreview(null); setRefVideoFiles([]); }
    if (!cfg.supports_audio_ref) { setRefAudioFiles([]); }
    if (!cfg.supports_first_last_frame) {
      setFirstFrameFile(null); setFirstFramePreview(null);
      setLastFrameFile(null); setLastFramePreview(null);
    }
    if (!cfg.supports_generate_audio) setGenerateAudio(false);
  };

  // Meme correctif que onProviderChange (mode normal) applique au mode storyboard.
  const onSceneProviderChange = (p) => {
    setSceneProvider(p);
    const cfg = providers[p];
    if (!cfg) return;
    setSceneResolution(r => cfg.resolutions.includes(r) ? r : cfg.resolutions[0]);
    setSceneAspectRatio(ar => cfg.aspect_ratios.includes(ar) ? ar : cfg.aspect_ratios[0]);
    setSceneDuration(d => Math.min(d, cfg.max_duration));
    if (!cfg.supports_video_ref) { setSceneVideoFile(null); setSceneVideoPreview(null); setSceneRefVideoFiles([]); }
    if (!cfg.supports_audio_ref) { setSceneRefAudioFiles([]); }
    if (!cfg.supports_first_last_frame) {
      setSceneFirstFrameFile(null); setSceneFirstFramePreview(null);
      setSceneLastFrameFile(null); setSceneLastFramePreview(null);
    }
    if (!cfg.supports_generate_audio) setSceneGenerateAudio(false);
  };

  // Clic sur une tuile du picker (mode normal). Pour la tuile groupee "TeckiA V" : si un tier
  // veo est deja actif on ne touche a rien (seul le selecteur de qualite doit permettre de
  // changer entre Lite/Fast/Quality), sinon on bascule sur veo_lite (le moins cher) comme
  // premier choix par defaut.
  const onProviderTileClick = (tileKey) => {
    if (tileKey === 'veo_group') {
      if (isVeoTierKey(provider)) return;
      onProviderChange('veo_lite');
      return;
    }
    onProviderChange(tileKey);
  };

  const onSceneProviderTileClick = (tileKey) => {
    if (tileKey === 'veo_group') {
      if (isVeoTierKey(sceneProvider)) return;
      onSceneProviderChange('veo_lite');
      return;
    }
    onSceneProviderChange(tileKey);
  };

  // Changement de qualite Lite/Fast/Quality au sein de la gamme TeckiA V : config identique
  // entre les 3 tiers, donc contrairement a onProviderChange on NE reinitialise PAS
  // resolution/duree/aspect_ratio/references deja choisis par l'utilisateur.
  const onTierChange = (tierKey) => { setProvider(tierKey); };
  const onSceneTierChange = (tierKey) => { setSceneProvider(tierKey); };

  const resetSceneMedia = () => {
    setSceneRefFiles([]); setSceneRefPreviews([]);
    setSceneVideoFile(null); setSceneVideoPreview(null);
    setSceneRefVideoFiles([]); setSceneRefAudioFiles([]);
    setSceneFirstFrameFile(null); setSceneFirstFramePreview(null);
    setSceneLastFrameFile(null); setSceneLastFramePreview(null);
  };

  const addSceneRefFiles = (fileList, max) => {
    const room = max - sceneRefFiles.length;
    if (room <= 0) return;
    const arr = Array.from(fileList).slice(0, room);
    setSceneRefFiles(prev => [...prev, ...arr]);
    arr.forEach(f => {
      const reader = new FileReader();
      reader.onload = e => setSceneRefPreviews(prev => [...prev, e.target.result]);
      reader.readAsDataURL(f);
    });
  };
  const removeSceneRef = (idx) => {
    setSceneRefFiles(prev => prev.filter((_, i) => i !== idx));
    setSceneRefPreviews(prev => prev.filter((_, i) => i !== idx));
  };

  // Toute vidéo de référence passe par VideoTrimModal (demande explicite utilisateur : le
  // bouton "Ajouter" ouvre le modal de découpage, pas un passthrough direct) — l'utilisateur
  // choisit toujours son segment, même si la vidéo tient déjà dans la limite du provider.
  // requestTrim() attend la résolution du modal (confirmation ou annulation) avant de rendre
  // la main à acceptVideoFile(s).
  // onAccept recoit (file, dureeSecondes) - meme principe que requestAudioTrim plus bas, pour
  // motion_control ou la duree video suit la duree de la video de reference fournie (pas de
  // slider libre, voir requires_video).
  const requestTrim = (file, maxDuration) => new Promise(resolve => {
    setTrimModal({
      file, maxDuration,
      onConfirm: (trimmedFile, dur) => { setTrimModal(null); resolve({ file: trimmedFile, duration: dur }); },
      onCancel: () => { setTrimModal(null); resolve(null); },
    });
  });

  const acceptVideoFile = async (file, maxDuration, onAccept) => {
    const trimmed = await requestTrim(file, maxDuration);
    if (trimmed) onAccept(trimmed.file, trimmed.duration);
  };

  const acceptVideoFiles = async (files, maxDuration, onAccept) => {
    for (const file of files) {
      // eslint-disable-next-line no-await-in-loop
      await acceptVideoFile(file, maxDuration, onAccept);
    }
  };

  // Meme principe que requestTrim/acceptVideoFile(s) ci-dessus, pour une reference AUDIO :
  // le modal s'ouvre systematiquement (previsualisation + decoupage) avant d'accepter le
  // fichier, jamais de passthrough direct.
  // onAccept recoit (file, dureeSecondes) - la duree vient du decoupage reel confirme dans
  // le modal, necessaire pour infinitalk ou la duree video suit la duree de l'audio (pas de
  // slider libre pour ce provider, voir requires_audio).
  const requestAudioTrim = (file, maxDuration) => new Promise(resolve => {
    setAudioTrimModal({
      file, maxDuration,
      onConfirm: (trimmedFile, dur) => { setAudioTrimModal(null); resolve({ file: trimmedFile, duration: dur }); },
      onCancel: () => { setAudioTrimModal(null); resolve(null); },
    });
  });

  const acceptAudioFile = async (file, maxDuration, onAccept) => {
    const trimmed = await requestAudioTrim(file, maxDuration);
    if (trimmed) onAccept(trimmed.file, trimmed.duration);
  };

  const acceptAudioFiles = async (files, maxDuration, onAccept) => {
    for (const file of files) {
      // eslint-disable-next-line no-await-in-loop
      await acceptAudioFile(file, maxDuration, onAccept);
    }
  };

  const onSceneVideoPicked = (file) => {
    if (!file) return;
    acceptVideoFile(file, sceneCfg?.max_duration, (f, dur) => {
      setSceneVideoFile(f);
      setSceneVideoPreview(URL.createObjectURL(f));
      if (sceneCfg?.requires_video && dur) setSceneDuration(Math.round(dur));
    });
  };
  const removeSceneVideo = () => { setSceneVideoFile(null); setSceneVideoPreview(null); };

  const addSceneRefVideoFiles = (fileList, max) => {
    const room = max - sceneRefVideoFiles.length;
    if (room <= 0) return;
    const arr = Array.from(fileList).slice(0, room);
    acceptVideoFiles(arr, sceneCfg?.max_duration, (f) => setSceneRefVideoFiles(prev => [...prev, f]));
  };
  const removeSceneRefVideo = (idx) => setSceneRefVideoFiles(prev => prev.filter((_, i) => i !== idx));

  const addSceneRefAudioFiles = (fileList, max) => {
    const room = max - sceneRefAudioFiles.length;
    if (room <= 0) return;
    const arr = Array.from(fileList).slice(0, room);
    acceptAudioFiles(arr, sceneCfg?.max_duration, (f, dur) => {
      setSceneRefAudioFiles(prev => [...prev, f]);
      if (sceneCfg?.requires_audio && dur) setSceneDuration(Math.round(dur));
    });
  };
  const removeSceneRefAudio = (idx) => setSceneRefAudioFiles(prev => prev.filter((_, i) => i !== idx));

  const onSceneFramePicked = (which, file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = e => {
      if (which === 'first') { setSceneFirstFrameFile(file); setSceneFirstFramePreview(e.target.result); }
      else { setSceneLastFrameFile(file); setSceneLastFramePreview(e.target.result); }
    };
    reader.readAsDataURL(file);
  };
  const removeSceneFrame = (which) => {
    if (which === 'first') { setSceneFirstFrameFile(null); setSceneFirstFramePreview(null); }
    else { setSceneLastFrameFile(null); setSceneLastFramePreview(null); }
  };

  const addRefFiles = (fileList) => {
    const room = (providerCfg?.max_images || 7) - refFiles.length;
    if (room <= 0) return;
    const arr = Array.from(fileList).slice(0, room);
    setRefFiles(prev => [...prev, ...arr]);
    arr.forEach(f => {
      const reader = new FileReader();
      reader.onload = e => setRefPreviews(prev => [...prev, e.target.result]);
      reader.readAsDataURL(f);
    });
  };

  const removeRef = (idx) => {
    setRefFiles(prev => prev.filter((_, i) => i !== idx));
    setRefPreviews(prev => prev.filter((_, i) => i !== idx));
  };

  const onVideoPicked = (file) => {
    if (!file) return;
    acceptVideoFile(file, providerCfg?.max_duration, (f, dur) => {
      setVideoFile(f);
      setVideoPreview(URL.createObjectURL(f));
      if (providerCfg?.requires_video && dur) setDuration(Math.round(dur));
    });
  };

  const removeVideo = () => { setVideoFile(null); setVideoPreview(null); };

  const addRefVideoFiles = (fileList, max) => {
    const room = max - refVideoFiles.length;
    if (room <= 0) return;
    const arr = Array.from(fileList).slice(0, room);
    acceptVideoFiles(arr, providerCfg?.max_duration, (f) => setRefVideoFiles(prev => [...prev, f]));
  };
  const removeRefVideo = (idx) => setRefVideoFiles(prev => prev.filter((_, i) => i !== idx));

  const addRefAudioFiles = (fileList, max) => {
    const room = max - refAudioFiles.length;
    if (room <= 0) return;
    const arr = Array.from(fileList).slice(0, room);
    acceptAudioFiles(arr, providerCfg?.max_duration, (f, dur) => {
      setRefAudioFiles(prev => [...prev, f]);
      if (providerCfg?.requires_audio && dur) setDuration(Math.round(dur));
    });
  };
  const removeRefAudio = (idx) => setRefAudioFiles(prev => prev.filter((_, i) => i !== idx));

  const onFramePicked = (which, file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = e => {
      if (which === 'first') { setFirstFrameFile(file); setFirstFramePreview(e.target.result); }
      else { setLastFrameFile(file); setLastFramePreview(e.target.result); }
    };
    reader.readAsDataURL(file);
  };
  const removeFrame = (which) => {
    if (which === 'first') { setFirstFrameFile(null); setFirstFramePreview(null); }
    else { setLastFrameFile(null); setLastFramePreview(null); }
  };

  const structuredSetters = {
    environment: setFieldEnvironment, ambiance: setFieldAmbiance, character: setFieldCharacter, outfit: setFieldOutfit,
    movement: setFieldMovement, action: setFieldAction, info: setFieldInfo,
  };

  const insertImageTag = (n) => {
    const tag = `@image${n}`;
    const setter = promptMode === 'structured' ? structuredSetters[structuredFocus] : setPrompt;
    setter(prev => {
      const trimmed = (prev || '').trimEnd();
      if (trimmed.includes(tag)) return prev;
      return trimmed ? `${trimmed} ${tag} ` : `${tag} `;
    });
  };

  // Attache une image directement a un champ detaille (normal mode) : rejoint la banque de
  // references partagee (plafond providerCfg.max_images, <=7) et insere son tag @imageN dans
  // ce champ precis, sans passer par le focus manuel + clic sur vignette.
  const attachImageToField = (key, fileList) => {
    const max = providerCfg?.max_images || 7;
    const room = max - refFiles.length;
    if (room <= 0) {
      window.alert(`Maximum ${max} images de référence atteint.`);
      return;
    }
    const arr = Array.from(fileList).slice(0, room);
    if (arr.length === 0) return;
    const tag = `@image${refFiles.length + 1}`;
    setRefFiles(prev => [...prev, ...arr]);
    arr.forEach(f => {
      const reader = new FileReader();
      reader.onload = e => setRefPreviews(prev => [...prev, e.target.result]);
      reader.readAsDataURL(f);
    });
    structuredSetters[key](prev => {
      const trimmed = (prev || '').trimEnd();
      if (trimmed.includes(tag)) return prev;
      return trimmed ? `${trimmed} ${tag} ` : `${tag} `;
    });
  };

  const openFieldImagePicker = (key) => {
    const max = providerCfg?.max_images || 7;
    if (refFiles.length >= max) {
      window.alert(`Maximum ${max} images de référence atteint.`);
      return;
    }
    setPendingFieldKey(key);
    fieldFileInputRef.current?.click();
  };

  const generatePromptFromBrief = async () => {
    if (!brief.trim() || generatingPrompt) return;
    setGeneratingPrompt(true);
    try {
      const formData = new FormData();
      formData.append('brief', brief.trim());
      refFiles.forEach(f => formData.append('reference_images', f));
      const res = await axios.post(`${API_URL}/api/ia-studio/video/generate-prompt`, formData, {
        headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' },
      });
      if (res.data.success) setPrompt(res.data.prompt);
    } catch (e) { /* silencieux */ }
    setGeneratingPrompt(false);
  };

  const stopPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
    pollRef.current = null;
    pollTimeoutRef.current = null;
  };

  const cancelGeneration = () => {
    stopPolling();
    setGenerating(false);
    setCurrentJob({ status: 'failed', error: 'Génération annulée.' });
  };

  const pollVideoStatus = (jobId) => {
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API_URL}/api/ia-studio/video/status/${jobId}`, { headers: authHeaders });
        const job = res.data;
        if (job.status === 'completed') {
          stopPolling();
          setCurrentJob({ status: 'completed', video_url: job.video_url });
          setGenerating(false);
          loadCredits(); loadHistory();
        } else if (job.status === 'failed') {
          stopPolling();
          setCurrentJob({ status: 'failed', error: job.error || 'Échec de la génération' });
          setGenerating(false);
        }
      } catch (e) { /* on retente au prochain tick */ }
    }, 4000);
  };

  const generate = async () => {
    if (!effectivePrompt.trim() || generating) return;
    stopPolling();
    setGenerating(true);
    setCurrentJob(null);
    try {
      const formData = new FormData();
      formData.append('provider', provider);
      if (promptMode === 'structured') {
        formData.append('field_environment', fieldEnvironment.trim());
        formData.append('field_ambiance', fieldAmbiance.trim());
        formData.append('field_character', fieldCharacter.trim());
        formData.append('field_outfit', fieldOutfit.trim());
        formData.append('field_movement', fieldMovement.trim());
        formData.append('field_action', fieldAction.trim());
        formData.append('field_info', fieldInfo.trim());
      } else {
        formData.append('prompt', prompt.trim());
      }
      formData.append('resolution', resolution);
      formData.append('aspect_ratio', aspectRatio);
      formData.append('duration', duration);
      formData.append('generate_audio', generateAudio ? 'true' : 'false');
      refFiles.forEach(f => formData.append('reference_images', f));
      if (videoFile) formData.append('reference_videos', videoFile);
      refVideoFiles.forEach(f => formData.append('reference_videos', f));
      refAudioFiles.forEach(f => formData.append('reference_audios', f));
      if (firstFrameFile) formData.append('first_frame', firstFrameFile);
      if (lastFrameFile) formData.append('last_frame', lastFrameFile);
      const res = await axios.post(`${API_URL}/api/ia-studio/video/generate`, formData, {
        headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' },
      });
      if (res.data.success) {
        setCurrentJob({ status: 'queued' });
        pollVideoStatus(res.data.job_id);
      } else {
        setGenerating(false);
      }
    } catch (e) {
      setGenerating(false);
      if (e?.response?.status === 402) {
        loadCredits();
        setCurrentJob({ status: 'failed', error: e.response.data?.message || 'Crédits insuffisants' });
      } else {
        setCurrentJob({ status: 'failed', error: e?.response?.data?.error || 'Erreur de génération' });
      }
    }
  };

  const pollActiveStoryboard = (storyboardId) => {
    if (scenePollRef.current) clearInterval(scenePollRef.current);
    scenePollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API_URL}/api/ia-studio/storyboard/${storyboardId}`, { headers: authHeaders });
        if (res.data.success) {
          setActiveStoryboard(res.data);
          const scenes = res.data.scenes || [];
          const last = scenes[scenes.length - 1];
          if (!last || last.status !== 'queued') {
            clearInterval(scenePollRef.current);
            loadCredits();
          }
        }
      } catch (e) { /* on retente */ }
    }, 4000);
  };

  const sceneStructuredSetters = {
    environment: setSceneFieldEnvironment, ambiance: setSceneFieldAmbiance, character: setSceneFieldCharacter, outfit: setSceneFieldOutfit,
    movement: setSceneFieldMovement, action: setSceneFieldAction, info: setSceneFieldInfo,
  };

  const attachImageToSceneField = (key, fileList) => {
    const max = sceneCfg?.max_images || 7;
    const room = max - sceneRefFiles.length;
    if (room <= 0) {
      window.alert(`Maximum ${max} images de référence atteint.`);
      return;
    }
    const arr = Array.from(fileList).slice(0, room);
    if (arr.length === 0) return;
    const tag = `@image${sceneRefFiles.length + 1}`;
    setSceneRefFiles(prev => [...prev, ...arr]);
    arr.forEach(f => {
      const reader = new FileReader();
      reader.onload = e => setSceneRefPreviews(prev => [...prev, e.target.result]);
      reader.readAsDataURL(f);
    });
    sceneStructuredSetters[key](prev => {
      const trimmed = (prev || '').trimEnd();
      if (trimmed.includes(tag)) return prev;
      return trimmed ? `${trimmed} ${tag} ` : `${tag} `;
    });
  };

  const openSceneFieldImagePicker = (key) => {
    const max = sceneCfg?.max_images || 7;
    if (sceneRefFiles.length >= max) {
      window.alert(`Maximum ${max} images de référence atteint.`);
      return;
    }
    setPendingSceneFieldKey(key);
    sceneFieldFileInputRef.current?.click();
  };

  const insertSceneImageTag = (n) => {
    const tag = `@image${n}`;
    const setter = scenePromptMode === 'structured' ? sceneStructuredSetters[sceneStructuredFocus] : setScenePrompt;
    setter(prev => {
      const trimmed = (prev || '').trimEnd();
      if (trimmed.includes(tag)) return prev;
      return trimmed ? `${trimmed} ${tag} ` : `${tag} `;
    });
  };

  const generatePromptForScene = async () => {
    if (!sceneBrief.trim() || generatingScenePrompt) return;
    setGeneratingScenePrompt(true);
    try {
      const formData = new FormData();
      formData.append('brief', sceneBrief.trim());
      sceneRefFiles.forEach(f => formData.append('reference_images', f));
      const res = await axios.post(`${API_URL}/api/ia-studio/video/generate-prompt`, formData, {
        headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' },
      });
      if (res.data.success) setScenePrompt(res.data.prompt);
    } catch (e) { /* silencieux */ }
    setGeneratingScenePrompt(false);
  };

  const addScene = async () => {
    if (!sceneEffectivePrompt.trim() || !activeStoryboard || addingScene) return;
    setAddingScene(true);
    try {
      const formData = new FormData();
      formData.append('provider', sceneProvider);
      if (scenePromptMode === 'structured') {
        formData.append('field_environment', sceneFieldEnvironment.trim());
        formData.append('field_ambiance', sceneFieldAmbiance.trim());
        formData.append('field_character', sceneFieldCharacter.trim());
        formData.append('field_outfit', sceneFieldOutfit.trim());
        formData.append('field_movement', sceneFieldMovement.trim());
        formData.append('field_action', sceneFieldAction.trim());
        formData.append('field_info', sceneFieldInfo.trim());
      } else {
        formData.append('prompt', scenePrompt.trim());
      }
      formData.append('resolution', sceneResolution);
      formData.append('aspect_ratio', sceneAspectRatio);
      formData.append('duration', sceneDuration);
      formData.append('generate_audio', sceneGenerateAudio ? 'true' : 'false');
      formData.append('use_last_frame', sceneUseLastFrame ? 'true' : 'false');
      sceneRefFiles.forEach(f => formData.append('reference_images', f));
      if (sceneVideoFile) formData.append('reference_videos', sceneVideoFile);
      sceneRefVideoFiles.forEach(f => formData.append('reference_videos', f));
      sceneRefAudioFiles.forEach(f => formData.append('reference_audios', f));
      if (sceneFirstFrameFile) formData.append('first_frame', sceneFirstFrameFile);
      if (sceneLastFrameFile) formData.append('last_frame', sceneLastFrameFile);
      const url = editingSceneId
        ? `${API_URL}/api/ia-studio/storyboard/${activeStoryboard.storyboard_id}/scene/${editingSceneId}/regenerate`
        : `${API_URL}/api/ia-studio/storyboard/${activeStoryboard.storyboard_id}/scene`;
      const res = await axios.post(url, formData, { headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' } });
      if (res.data.success) {
        setScenePrompt(''); setSceneBrief(''); resetSceneMedia();
        setSceneFieldEnvironment(''); setSceneFieldAmbiance(''); setSceneFieldOutfit('');
        setSceneFieldMovement(''); setSceneFieldAction('');
        setEditingSceneId(null);
        await openStoryboard(activeStoryboard.storyboard_id);
        pollActiveStoryboard(activeStoryboard.storyboard_id);
      }
    } catch (e) {
      if (e?.response?.status === 402) { loadCredits(); setShowTopup(true); }
      else window.alert(e?.response?.data?.error || e?.response?.data?.message || "Échec de l'ajout de la scène.");
    }
    setAddingScene(false);
  };

  // Pre-remplit le composeur "Nouvelle scene" avec les reglages d'une scene DEJA terminee/
  // echouee pour la regenerer en remplacement (pas en plus) - voir editingSceneId. Les
  // references (photos/videos/audios) ne sont pas retrouvables depuis la scene deja generee,
  // l'utilisateur devra les rejoindre lui-meme s'il en veut a nouveau.
  const startEditScene = (scene) => {
    setEditingSceneId(scene.scene_id);
    setSceneProvider(scene.provider);
    setSceneResolution(scene.resolution);
    setSceneDuration(scene.duration);
    setSceneAspectRatio(providers[scene.provider]?.aspect_ratios?.[0] || '');
    setScenePromptMode('free');
    setScenePrompt(scene.prompt || '');
    setSceneBrief('');
    resetSceneMedia();
    document.getElementById('scene-composer')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const cancelEditScene = () => {
    setEditingSceneId(null);
    setScenePrompt(''); setSceneBrief(''); resetSceneMedia();
  };

  const assembleFilm = async () => {
    if (!activeStoryboard || assembling) return;
    setAssembling(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/ia-studio/storyboard/${activeStoryboard.storyboard_id}/assemble`, {}, { headers: authHeaders },
      );
      if (res.data.success) await openStoryboard(activeStoryboard.storyboard_id);
    } catch (e) {
      window.alert(e?.response?.data?.error || "Échec de l'assemblage");
    }
    setAssembling(false);
  };

  // Paiement carte reussi cote Stripe (stripe.confirmPayment) - le credit reel se fait
  // ensuite cote serveur via le webhook /api/stripe/webhook (payment_intent.succeeded), donc
  // on relance loadCredits() a quelques secondes d'intervalle plutot qu'une seule fois pour
  // laisser le temps au webhook d'arriver.
  const handleCardTopupSuccess = () => {
    setShowTopup(false);
    window.alert('Paiement réussi ! Votre solde sera mis à jour dans quelques secondes.');
    loadCredits();
    setTimeout(loadCredits, 3000);
    setTimeout(loadCredits, 8000);
  };

  const submitTopup = async (amount, method, ref) => {
    if (!amount || !ref.trim() || submittingTopup) return;
    setSubmittingTopup(true);
    try {
      await axios.post(`${API_URL}/api/ia-studio/video/credits/order`, {
        amount_ar: parseInt(amount, 10), method, payment_ref: ref.trim(),
      }, { headers: authHeaders });
      setShowTopup(false);
      window.alert('Demande envoyée ! Votre solde sera crédité après vérification du paiement.');
    } catch (e) {
      window.alert(e?.response?.data?.error || 'Erreur');
    }
    setSubmittingTopup(false);
  };

  const providerCfg = providers[provider];
  const sceneCfg = providers[sceneProvider];

  // Tuiles du picker : les 3 tiers veo_* (config strictement identique cote backend) sont
  // regroupes en une seule tuile "veo_group" ("TeckiA V"), les autres providers restent
  // inchanges. Partage entre mode normal et storyboard (meme etat `providers`).
  const providerTiles = (() => {
    const tiles = [];
    let veoAdded = false;
    Object.entries(providers).forEach(([key, cfg]) => {
      if (isVeoTierKey(key)) {
        if (!veoAdded) {
          const defaultTierKey = VEO_TIERS.map(t => t.key).find(k => providers[k]);
          tiles.push({ key: 'veo_group', cfg: providers[defaultTierKey] });
          veoAdded = true;
        }
      } else {
        tiles.push({ key, cfg });
      }
    });
    return tiles;
  })();
  const scenes = activeStoryboard?.scenes || [];
  const completedScenesCount = scenes.filter(s => s.status === 'completed').length;
  const lastSceneQueued = scenes.length > 0 && scenes[scenes.length - 1].status === 'queued';

  // Cout estime cote client, meme formule que _video_price_for (backend) : forfait par
  // resolution pour omni (ignore la duree), sinon prix/seconde x duree pour les 3 autres.
  // Omni a un tarif majore si une video de reference est jointe (cout Kie plus eleve) -
  // voir cfg.pricing_with_video / _resolution_price_key cote backend.
  const estimatePrice = (cfg, resolution, duration, hasVideoRef) => {
    if (!cfg || !cfg.pricing) return 0;
    const withVideo = hasVideoRef && cfg.pricing_with_video && cfg.pricing_with_video[resolution] != null;
    const rate = (withVideo ? cfg.pricing_with_video[resolution] : cfg.pricing[resolution]) || 0;
    return cfg.flat_pricing ? rate : rate * duration;
  };
  const hasNormalVideoRef = !!videoFile || refVideoFiles.length > 0;
  const hasSceneVideoRef = !!sceneVideoFile || sceneRefVideoFiles.length > 0;
  const normalPrice = estimatePrice(providerCfg, resolution, duration, hasNormalVideoRef);
  const scenePrice = estimatePrice(sceneCfg, sceneResolution, sceneDuration, hasSceneVideoRef);

  const structuredJoined = [fieldEnvironment, fieldAmbiance, fieldCharacter, fieldOutfit, fieldMovement, fieldAction, fieldInfo].filter(s => s.trim()).join(' ');
  const effectivePrompt = promptMode === 'structured' ? structuredJoined : prompt;
  const sceneStructuredJoined = [sceneFieldEnvironment, sceneFieldAmbiance, sceneFieldCharacter, sceneFieldOutfit, sceneFieldMovement, sceneFieldAction, sceneFieldInfo].filter(s => s.trim()).join(' ');
  const sceneEffectivePrompt = scenePromptMode === 'structured' ? sceneStructuredJoined : scenePrompt;

  return (
    <div className="min-h-screen bg-[#07050d] pb-16 relative">
      <div className="pointer-events-none fixed -top-32 -left-32 w-72 h-72 bg-purple-700/10 rounded-full blur-3xl" />
      <div className="pointer-events-none fixed top-32 -right-32 w-72 h-72 bg-fuchsia-700/[0.07] rounded-full blur-3xl" />

      <div className="sticky top-0 z-40 bg-[#07050d]/95 backdrop-blur-md border-b border-white/5">
        <div className={`mx-auto px-4 pt-4 pb-3 transition-all duration-300 ${mode === 'storyboard' ? 'max-w-7xl' : 'max-w-5xl'}`}>
          <div className="flex items-center justify-between mb-3">
            <button onClick={onBack} className="flex items-center gap-2 text-white/70 hover:text-white transition text-sm">
              <ArrowLeft className="w-4 h-4" /> Retour
            </button>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowMyStats(true)}
                title="Mes statistiques"
                className="flex items-center justify-center w-8 h-8 rounded-full border bg-white/5 text-gray-300 border-white/10 hover:border-amber-400/50 hover:text-amber-300 transition"
              >
                <BarChart3 className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => setShowTopup(true)}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border bg-white/5 text-gray-300 border-white/10 hover:border-purple-400/50 transition"
              >
                <Coins className="w-3.5 h-3.5 text-amber-300" /> {credits.toLocaleString('fr-FR')} Ar
              </button>
            </div>
          </div>

          <h1 className="text-xl font-black tracking-tight mb-3 flex items-center justify-center gap-2">
            <Film className="w-5 h-5 text-purple-400 drop-shadow-[0_0_10px_rgba(168,85,247,0.6)]" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-200 via-fuchsia-200 to-purple-200">TeckiA IA Studio Vidéo</span>
          </h1>

          <div className="flex gap-2 bg-white/5 border border-white/10 rounded-xl p-1">
            <button
              onClick={() => setMode('normal')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-semibold transition ${mode === 'normal' ? 'bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white shadow-[0_0_16px_rgba(147,51,234,0.4)]' : 'text-gray-400 hover:text-gray-200'}`}
            >
              <Wand2 className="w-4 h-4" /> Génération normale
            </button>
            <button
              onClick={() => setMode('storyboard')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-semibold transition ${mode === 'storyboard' ? 'bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white shadow-[0_0_16px_rgba(147,51,234,0.4)]' : 'text-gray-400 hover:text-gray-200'}`}
            >
              <Clapperboard className="w-4 h-4" /> Storyboard
            </button>
          </div>
        </div>
      </div>

      <div className={`mx-auto px-4 pt-6 relative transition-all duration-300 ${mode === 'storyboard' ? 'max-w-7xl' : 'max-w-5xl'}`}>
        {mode === 'normal' && (
          <div className="grid lg:grid-cols-[300px_1fr] gap-5 items-start">
            {/* Colonne gauche : vidéos générées, toujours visible, défile indépendamment du formulaire */}
            <div className="lg:sticky lg:top-44 order-2 lg:order-1">
              <div className="relative bg-[#140a24]/95 border border-purple-500/25 rounded-2xl p-4 shadow-[0_0_40px_rgba(147,51,234,0.15)] mb-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-white font-bold text-sm flex items-center gap-1.5">
                    <Film className="w-4 h-4 text-purple-300" /> Vos vidéos
                  </div>
                  <button onClick={loadHistory} className="text-gray-400 hover:text-white transition hover:rotate-180 duration-500">
                    <RefreshCw className="w-4 h-4" />
                  </button>
                </div>

                {currentJob && (
                  <div className="mb-4 text-center">
                    {currentJob.status === 'queued' && (
                      <>
                        <TeckiALoader subtitle="Ça peut prendre 1 à 5 minutes selon le style choisi" />
                        <button onClick={cancelGeneration} className="text-xs text-gray-400 hover:text-red-300 transition -mt-4 mb-2">Annuler</button>
                      </>
                    )}
                    {currentJob.status === 'failed' && <div className="py-4 text-red-300 text-sm">{currentJob.error}</div>}
                    {currentJob.status === 'completed' && (
                      <>
                        <video src={currentJob.video_url} controls className="w-full rounded-xl mb-3" />
                        <a
                          href={currentJob.video_url} download target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white text-xs font-semibold transition hover:scale-105"
                        >
                          <Download className="w-3.5 h-3.5" /> Télécharger
                        </a>
                      </>
                    )}
                  </div>
                )}

                <div className="max-h-[55vh] overflow-y-auto pr-1 space-y-3">
                  {loadingHistory ? (
                    <div className="text-center text-gray-400 text-sm py-8">Chargement...</div>
                  ) : history.length === 0 ? (
                    <div className="text-center text-gray-400 text-sm py-8 flex flex-col items-center gap-2">
                      <Film className="w-8 h-8 text-gray-700" /> Aucune vidéo générée pour l&apos;instant.
                    </div>
                  ) : (
                    history.map(item => (
                      <div key={item.job_id} className="relative rounded-xl overflow-hidden border border-white/10">
                        <video src={item.video_url} controls className="w-full aspect-video object-contain bg-black" />
                      </div>
                    ))
                  )}
                </div>
              </div>
              <Retention14DaysNotice />
            </div>

            {/* Colonne droite : formulaire de génération, reste fixe pendant que la colonne gauche défile */}
            <div className="order-1 lg:order-2">
            <div className="relative bg-[#140a24]/95 border border-purple-500/15 rounded-2xl p-5 mb-6 shadow-[0_0_40px_rgba(147,51,234,0.08)]">
              <label className="block text-white font-semibold mb-2 text-sm">Style</label>
              <div className="grid grid-cols-2 gap-2 mb-4">
                {providerTiles.map(({ key, cfg }) => {
                  const active = key === 'veo_group' ? isVeoTierKey(provider) : provider === key;
                  const label = key === 'veo_group' ? 'TeckiA V' : cfg.label;
                  const info = key === 'veo_group' ? VEO_GROUP_INFO : cfg.info;
                  const theme = providerTheme(key);
                  const Icon = theme.icon;
                  return (
                    <div key={key} className="relative group" style={{ perspective: '700px' }}>
                      {key === 'seedance_mini' && (
                        <span className="absolute -top-2 right-2 z-10 px-1.5 py-0.5 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-[9px] font-bold shadow-[0_0_8px_rgba(16,185,129,0.6)]">
                          ⭐ Meilleur choix
                        </span>
                      )}
                      <button
                        onClick={() => onProviderTileClick(key)}
                        className={`relative w-full overflow-hidden px-3 py-2.5 rounded-xl text-xs font-bold border text-left transition-all duration-300 group-hover:-translate-y-0.5 ${
                          active
                            ? `bg-gradient-to-br ${theme.color} border-white/40 text-white ${theme.glow}`
                            : 'bg-white/5 border-white/10 text-gray-300 hover:border-white/25'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <div className={`flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center bg-gradient-to-br ${theme.color} ${active ? 'shadow-[inset_0_1px_1px_rgba(255,255,255,0.35)]' : ''}`}>
                            <Icon className="w-3.5 h-3.5 text-white" />
                          </div>
                          <div className="min-w-0">
                            <div className="truncate">{label}</div>
                            <div className="text-[10px] font-normal opacity-70 mt-0.5">jusqu'à {cfg.max_duration}s</div>
                          </div>
                        </div>
                      </button>
                      {info && (
                        <div className="pointer-events-none absolute z-20 left-0 right-0 top-full mt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-150">
                          <div className="bg-[#1c0f30] border border-purple-500/30 rounded-xl p-3 text-[11px] text-gray-300 leading-relaxed shadow-2xl">
                            {info}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="flex gap-2 mb-4 bg-black/20 border border-white/10 rounded-xl p-1">
                <button
                  onClick={() => setPromptMode('free')}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-bold transition ${promptMode === 'free' ? 'bg-purple-600 text-white shadow-[0_0_12px_rgba(147,51,234,0.4)]' : 'text-gray-400 hover:text-gray-200'}`}
                >
                  <MessageSquare className="w-3.5 h-3.5" /> Prompt libre
                </button>
                <button
                  onClick={() => setPromptMode('structured')}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-bold transition ${promptMode === 'structured' ? 'bg-purple-600 text-white shadow-[0_0_12px_rgba(147,51,234,0.4)]' : 'text-gray-400 hover:text-gray-200'}`}
                >
                  <SlidersHorizontal className="w-3.5 h-3.5" /> Champs détaillés
                </button>
              </div>

              {promptMode === 'free' ? (
              <>
              <label className="flex items-center gap-1.5 text-white font-semibold mb-2 text-sm">
                Que voulez-vous ? <span className="text-[10px] font-normal text-gray-500">(décrivez librement, TeckiA rédige le prompt)</span>
              </label>
              <div className="flex gap-2 mb-4">
                <textarea
                  value={brief}
                  onChange={e => setBrief(e.target.value)}
                  rows={2}
                  placeholder="Ex : une pub pour mon parfum, ambiance luxueuse, coucher de soleil..."
                  className="flex-1 bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-purple-400 resize-y min-h-[76px] transition"
                />
                <button
                  onClick={generatePromptFromBrief}
                  disabled={!brief.trim() || generatingPrompt}
                  title="Générer le prompt"
                  className="flex-shrink-0 w-11 rounded-xl bg-purple-600 hover:bg-purple-500 disabled:opacity-40 flex items-center justify-center transition"
                >
                  {generatingPrompt ? <Loader2 className="w-4 h-4 text-white animate-spin" /> : <MessageSquarePlus className="w-4 h-4 text-white" />}
                </button>
              </div>

              <label className="block text-white font-semibold mb-2 text-sm">Prompt final</label>
              <textarea
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                rows={3}
                placeholder="Ex : Un flacon de parfum qui tourne lentement sur un rocher au coucher du soleil..."
                className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-purple-400 focus:shadow-[0_0_0_3px_rgba(147,51,234,0.15)] resize-y min-h-[76px] transition"
              />
              </>
              ) : (
              <div className="mb-4">
                <div className="flex items-center gap-1.5 text-white font-semibold mb-3 text-sm">
                  <SlidersHorizontal className="w-3.5 h-3.5 text-purple-300" />
                  Champs détaillés
                  <span className="text-[10px] font-normal text-gray-500 ml-1">— remplissez ce qui compte, laissez le reste vide</span>
                </div>
                <div className="space-y-3">
                  {STRUCTURED_FIELDS.map(f => {
                    const value = { environment: fieldEnvironment, ambiance: fieldAmbiance, character: fieldCharacter, outfit: fieldOutfit, movement: fieldMovement, action: fieldAction, info: fieldInfo }[f.key];
                    const setter = structuredSetters[f.key];
                    return (
                      <div key={f.key}>
                        <label className="block text-gray-400 text-xs font-semibold mb-1">{f.label}</label>
                        <div className="flex items-center gap-1.5">
                          <input
                            value={value}
                            onChange={e => setter(e.target.value)}
                            onFocus={() => setStructuredFocus(f.key)}
                            placeholder={f.placeholder}
                            className="w-full bg-black/30 border border-white/10 rounded-xl px-3.5 py-2.5 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-purple-400 transition"
                          />
                          <button
                            type="button"
                            onClick={() => openFieldImagePicker(f.key)}
                            title={`Attacher une image à ${f.label}`}
                            disabled={refFiles.length >= (providerCfg?.max_images || 7)}
                            className="flex-shrink-0 w-9 h-9 rounded-xl border border-white/10 bg-white/5 hover:bg-purple-600/30 hover:border-purple-400/50 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center text-gray-400 hover:text-purple-300 transition"
                          >
                            <Paperclip className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <input
                  type="file" accept="image/*" multiple ref={fieldFileInputRef} className="hidden"
                  onChange={e => {
                    if (pendingFieldKey) attachImageToField(pendingFieldKey, e.target.files);
                    e.target.value = '';
                    setPendingFieldKey(null);
                  }}
                />
              </div>
              )}

              {providerCfg && isVeoTierKey(provider) && (
                <div className="mt-4">
                  <label className="block text-white font-semibold mb-2 text-sm">Qualité TeckiA V</label>
                  <div className="flex gap-1.5">
                    {VEO_TIERS.filter(t => providers[t.key]).map(t => (
                      <button
                        key={t.key}
                        onClick={() => onTierChange(t.key)}
                        className={`flex-1 px-3 py-2 rounded-lg text-xs font-bold border transition ${
                          provider === t.key
                            ? 'bg-gradient-to-r from-purple-600 to-fuchsia-600 border-purple-400 text-white shadow-[0_0_16px_rgba(147,51,234,0.5)]'
                            : 'bg-white/5 border-white/10 text-gray-300 hover:border-white/25'
                        }`}
                      >
                        {t.short}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {providerCfg && (
                <div className="grid grid-cols-2 gap-3 mt-4">
                  <div>
                    <label className="block text-white font-semibold mb-2 text-sm">Résolution</label>
                    <div className="flex flex-wrap gap-1.5">
                      {providerCfg.resolutions.map(r => (
                        <button
                          key={r} onClick={() => {
                            setResolution(r);
                            // Certaines resolutions (360p Omni via fal.ai) ne supportent pas de
                            // video de reference - on vide la selection pour eviter qu'elle soit
                            // silencieusement ignoree cote backend.
                            if (providerCfg.video_ref_unsupported_resolutions?.includes(r)) {
                              setVideoFile(null); setVideoPreview(null); setRefVideoFiles([]);
                            }
                          }}
                          className={`px-2.5 py-1.5 rounded-lg text-xs font-bold border transition ${resolution === r ? 'bg-purple-600 border-purple-400 text-white' : 'bg-white/5 border-white/10 text-gray-400'}`}
                        >
                          {r}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-white font-semibold mb-2 text-sm">Durée : {duration}s</label>
                    {provider === 'omni' ? (
                      <p className="text-[11px] text-gray-500 mt-1.5">Durée fixe (même prix quelle que soit la durée choisie).</p>
                    ) : providerCfg?.requires_audio ? (
                      <p className="text-[11px] text-gray-500 mt-1.5">Suit la durée de l'audio fourni (max {providerCfg?.max_duration}s).</p>
                    ) : providerCfg?.requires_video ? (
                      <p className="text-[11px] text-gray-500 mt-1.5">Suit la durée de la vidéo fournie (max {providerCfg?.max_duration}s).</p>
                    ) : (
                      <>
                        <input
                          type="range" min={1} max={providerCfg.max_duration} value={duration}
                          onChange={e => setDuration(parseInt(e.target.value, 10))}
                          className="w-full accent-purple-500"
                          disabled={!!videoFile}
                        />
                        {videoFile && <p className="text-[10px] text-gray-500 mt-1">Ignorée : la durée suit la vidéo fournie</p>}
                      </>
                    )}
                  </div>
                </div>
              )}

              {providerCfg && providerCfg.aspect_ratios.length > 1 && (
                <div className="mt-4">
                  <label className="block text-white font-semibold mb-2 text-sm">Format</label>
                  <div className="flex flex-wrap gap-1.5">
                    {providerCfg.aspect_ratios.map(ar => (
                      <button
                        key={ar} onClick={() => setAspectRatio(ar)}
                        className={`px-2.5 py-1.5 rounded-lg text-xs font-bold border transition ${aspectRatio === ar ? 'bg-purple-600 border-purple-400 text-white' : 'bg-white/5 border-white/10 text-gray-400'}`}
                      >
                        {ar}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {providerCfg?.supports_generate_audio && (
                <div className="mt-4 flex items-center justify-between bg-black/20 border border-white/10 rounded-xl px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Music2 className="w-4 h-4 text-purple-300" />
                    <span className="text-white text-sm font-semibold">Générer l'audio automatiquement</span>
                  </div>
                  <button
                    onClick={() => setGenerateAudio(v => !v)}
                    className={`w-11 h-6 rounded-full transition relative flex-shrink-0 ${generateAudio ? 'bg-purple-600' : 'bg-white/10'}`}
                  >
                    <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${generateAudio ? 'translate-x-5' : 'translate-x-0.5'}`} />
                  </button>
                </div>
              )}

              <div className="mt-4">
                <label className="block text-white font-semibold mb-2 text-sm">
                  Photo{providerCfg?.max_images > 1 ? 's de référence' : ''} {providerCfg?.requires_image ? '(requis)' : `(optionnel, jusqu'à ${providerCfg?.max_images || 7})`}
                </label>
                {refPreviews.length > 0 && (
                  <p className="text-[11px] text-gray-500 mb-2">
                    Touchez une photo pour l'insérer {promptMode === 'structured' ? (
                      <>dans le champ <span className="text-purple-300">{STRUCTURED_FIELDS.find(f => f.key === structuredFocus)?.label}</span></>
                    ) : 'dans le prompt'} (ex: <span className="text-purple-300">@image1</span>)
                  </p>
                )}
                <div className="flex flex-wrap gap-3">
                  {refPreviews.map((src, idx) => (
                    <button
                      key={idx} type="button" onClick={() => insertImageTag(idx + 1)}
                      className="relative w-20 h-20 rounded-xl overflow-hidden border border-purple-500/25 hover:border-purple-400 transition group"
                    >
                      <img src={src} alt="" className="w-full h-full object-cover" />
                      <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-center py-0.5">
                        <span className="text-[9px] font-bold text-purple-300">@image{idx + 1}</span>
                      </div>
                      <div
                        role="button"
                        onClick={e => { e.stopPropagation(); removeRef(idx); }}
                        className="absolute top-1 right-1 w-5 h-5 bg-black/70 hover:bg-red-600 rounded-full flex items-center justify-center"
                      >
                        <X className="w-3 h-3 text-white" />
                      </div>
                    </button>
                  ))}
                  {refFiles.length < (providerCfg?.max_images || 7) && (
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="w-20 h-20 rounded-xl border-2 border-dashed border-white/20 flex flex-col items-center justify-center text-gray-400 hover:text-purple-300 hover:border-purple-400/50 transition"
                    >
                      <Upload className="w-5 h-5 mb-1" />
                      <span className="text-[10px]">Ajouter</span>
                    </button>
                  )}
                  <input
                    ref={fileInputRef} type="file" accept="image/*" multiple className="hidden"
                    onChange={e => { addRefFiles(e.target.files); e.target.value = ''; }}
                  />
                </div>
              </div>

              {providerCfg?.supports_video_ref && providerCfg.max_videos === 1
                && !providerCfg.video_ref_unsupported_resolutions?.includes(resolution) && (
                <div className="mt-4">
                  <label className="flex items-center gap-1.5 text-white font-semibold mb-2 text-sm">
                    <Video className="w-4 h-4 text-purple-300" /> Vidéo de référence {providerCfg.requires_video ? `(requis, max ${providerCfg.max_duration}s)` : '(optionnel)'}
                  </label>
                  <p className="text-[11px] text-gray-500 mb-2">
                    {providerCfg.requires_video
                      ? "Vidéo de mouvement/performance à reproduire sur votre photo."
                      : "Transforme une vidéo existante selon votre prompt, au lieu de partir de zéro."}
                  </p>
                  {videoPreview ? (
                    <div className="relative inline-block">
                      <video src={videoPreview} className="w-40 h-24 rounded-xl object-contain bg-black border border-purple-500/25" muted />
                      <button onClick={removeVideo} className="absolute top-1 right-1 w-6 h-6 bg-black/70 hover:bg-red-600 rounded-full flex items-center justify-center">
                        <X className="w-3.5 h-3.5 text-white" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => videoInputRef.current?.click()}
                      className="w-40 h-24 rounded-xl border-2 border-dashed border-white/20 flex flex-col items-center justify-center text-gray-400 hover:text-purple-300 hover:border-purple-400/50 transition"
                    >
                      <Video className="w-5 h-5 mb-1" />
                      <span className="text-[10px]">Ajouter une vidéo</span>
                    </button>
                  )}
                  <input
                    ref={videoInputRef} type="file" accept="video/*" className="hidden"
                    onChange={e => { onVideoPicked(e.target.files?.[0]); e.target.value = ''; }}
                  />
                </div>
              )}

              {providerCfg?.supports_first_last_frame && (
                <div className="mt-4">
                  <label className="flex items-center gap-1.5 text-white font-semibold mb-2 text-sm">
                    <ImagePlus className="w-4 h-4 text-purple-300" /> Première / dernière image (optionnel)
                  </label>
                  <div className="flex gap-4">
                    {[{ key: 'first', label: 'Première', preview: firstFramePreview, ref: firstFrameInputRef },
                      { key: 'last', label: 'Dernière', preview: lastFramePreview, ref: lastFrameInputRef }].map(f => (
                      <div key={f.key}>
                        {f.preview ? (
                          <div className="relative w-20 h-20 rounded-xl overflow-hidden border border-purple-500/25">
                            <img src={f.preview} alt="" className="w-full h-full object-cover" />
                            <button onClick={() => removeFrame(f.key)} className="absolute top-1 right-1 w-5 h-5 bg-black/70 hover:bg-red-600 rounded-full flex items-center justify-center">
                              <X className="w-3 h-3 text-white" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => f.ref.current?.click()}
                            className="w-20 h-20 rounded-xl border-2 border-dashed border-white/20 flex flex-col items-center justify-center text-gray-400 hover:text-purple-300 hover:border-purple-400/50 transition"
                          >
                            <Upload className="w-4 h-4 mb-1" />
                            <span className="text-[9px]">{f.label}</span>
                          </button>
                        )}
                        <input
                          ref={f.ref} type="file" accept="image/*" className="hidden"
                          onChange={e => { onFramePicked(f.key, e.target.files?.[0]); e.target.value = ''; }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {providerCfg?.supports_video_ref && providerCfg.max_videos > 1 && (
                <div className="mt-4">
                  <label className="flex items-center gap-1.5 text-white font-semibold mb-2 text-sm">
                    <Video className="w-4 h-4 text-purple-300" /> Vidéos de référence (optionnel, jusqu'à {providerCfg.max_videos})
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {refVideoFiles.map((f, idx) => (
                      <div key={idx} className="flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-gray-300">
                        <Video className="w-3.5 h-3.5 text-purple-300" /> {f.name.slice(0, 18)}
                        <button onClick={() => removeRefVideo(idx)}><X className="w-3 h-3 text-gray-400 hover:text-red-400" /></button>
                      </div>
                    ))}
                    {refVideoFiles.length < providerCfg.max_videos && (
                      <button
                        onClick={() => refVideoInputRef.current?.click()}
                        className="flex items-center gap-1.5 border-2 border-dashed border-white/20 rounded-lg px-3 py-1.5 text-xs text-gray-400 hover:text-purple-300 hover:border-purple-400/50 transition"
                      >
                        <Upload className="w-3.5 h-3.5" /> Ajouter
                      </button>
                    )}
                  </div>
                  <input
                    ref={refVideoInputRef} type="file" accept="video/*" multiple className="hidden"
                    onChange={e => { addRefVideoFiles(e.target.files, providerCfg.max_videos); e.target.value = ''; }}
                  />
                </div>
              )}

              {providerCfg?.supports_audio_ref && (
                <div className="mt-4">
                  <label className="flex items-center gap-1.5 text-white font-semibold mb-2 text-sm">
                    <Music2 className="w-4 h-4 text-purple-300" /> Audio{providerCfg.max_audios > 1 ? 's de référence' : ''} {providerCfg.requires_audio ? `(requis, max ${providerCfg.max_duration}s)` : `(optionnel, jusqu'à ${providerCfg.max_audios})`}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {refAudioFiles.map((f, idx) => (
                      <div key={idx} className="flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-gray-300">
                        <Music2 className="w-3.5 h-3.5 text-purple-300" /> {f.name.slice(0, 18)}
                        <button onClick={() => removeRefAudio(idx)}><X className="w-3 h-3 text-gray-400 hover:text-red-400" /></button>
                      </div>
                    ))}
                    {refAudioFiles.length < providerCfg.max_audios && (
                      <button
                        onClick={() => refAudioInputRef.current?.click()}
                        className="flex items-center gap-1.5 border-2 border-dashed border-white/20 rounded-lg px-3 py-1.5 text-xs text-gray-400 hover:text-purple-300 hover:border-purple-400/50 transition"
                      >
                        <Upload className="w-3.5 h-3.5" /> Ajouter
                      </button>
                    )}
                  </div>
                  <input
                    ref={refAudioInputRef} type="file" multiple className="hidden"
                    onChange={e => { addRefAudioFiles(e.target.files, providerCfg.max_audios); e.target.value = ''; }}
                  />
                </div>
              )}

              {providerCfg && (
                <div className={`mt-5 flex items-center justify-between rounded-xl px-4 py-3 border ${
                  normalPrice > credits ? 'bg-red-500/10 border-red-500/30' : 'bg-black/20 border-white/10'
                }`}>
                  <span className="text-gray-400 text-xs flex items-center gap-1.5">
                    <Coins className="w-3.5 h-3.5" /> Coût de cette génération
                  </span>
                  <span className={`font-bold text-sm ${normalPrice > credits ? 'text-red-300' : 'text-amber-300'}`}>
                    {normalPrice.toLocaleString('fr-FR')} Ar
                  </span>
                </div>
              )}

              <button
                onClick={generate}
                disabled={!effectivePrompt.trim() || generating}
                className="group w-full mt-3 py-3.5 rounded-xl font-bold text-white flex items-center justify-center gap-2 transition disabled:opacity-40 disabled:cursor-not-allowed hover:scale-[1.015] active:scale-[0.99] shadow-[0_8px_24px_rgba(147,51,234,0.35)]"
                style={{ background: 'linear-gradient(135deg, #9333EA, #DB2777, #9333EA)', backgroundSize: '200% 200%' }}
              >
                {generating ? <>Génération en cours...</> : <><Film className="w-5 h-5 group-hover:rotate-12 transition-transform" /> Générer la vidéo</>}
              </button>
            </div>
            </div>
          </div>
        )}

        {mode === 'storyboard' && (
          <div className="grid lg:grid-cols-[250px_1fr_340px] gap-5 items-start">
            {/* Colonne gauche : liste des storyboards, toujours visible */}
            <div className="lg:sticky lg:top-44">
              <div className="bg-[#140a24]/95 border border-purple-500/15 rounded-2xl p-4 shadow-[0_0_30px_rgba(147,51,234,0.08)]">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-white font-bold text-sm flex items-center gap-1.5">
                    <Clapperboard className="w-4 h-4 text-purple-300" /> Vos storyboards
                  </span>
                  <span className="text-[10px] text-gray-500 font-semibold">{storyboards.length}/5</span>
                </div>
                <button
                  onClick={createStoryboard}
                  disabled={storyboards.length >= 5}
                  className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-bold text-white mb-3 transition hover:scale-[1.02] disabled:opacity-40 disabled:hover:scale-100"
                  style={{ background: 'linear-gradient(135deg, #9333EA, #DB2777)' }}
                >
                  {storyboards.length >= 5 ? <Lock className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                  {storyboards.length >= 5 ? 'Limite atteinte' : 'Nouveau storyboard'}
                </button>
                {storyboards.length >= 5 && (
                  <p className="text-[10px] text-gray-500 mb-3 leading-relaxed">Limite de 5 storyboards par compte pour l'instant. Une offre pour en avoir plus arrive bientôt.</p>
                )}
                {loadingStoryboards ? (
                  <div className="text-center text-gray-400 text-xs py-6">Chargement...</div>
                ) : storyboards.length === 0 ? (
                  <div className="text-center text-gray-500 text-xs py-6">Aucun storyboard pour l'instant.</div>
                ) : (
                  <div className="flex flex-col gap-2 max-h-[60vh] overflow-y-auto pr-1">
                    {storyboards.map(sb => (
                      <button
                        key={sb.storyboard_id}
                        onClick={() => openStoryboard(sb.storyboard_id)}
                        className={`text-left px-3 py-2.5 rounded-xl border text-xs transition ${activeStoryboard?.storyboard_id === sb.storyboard_id ? 'bg-purple-600/20 border-purple-400 text-white shadow-[0_0_16px_rgba(147,51,234,0.25)]' : 'bg-white/5 border-white/10 text-gray-300 hover:border-white/25'}`}
                      >
                        <div className="font-semibold truncate">{sb.title}</div>
                        <div className="text-[10px] text-gray-500 mt-0.5 flex items-center gap-1">
                          <Layers className="w-3 h-3" /> {sb.scene_count} scène(s)
                          {sb.final_video_url && <Check className="w-3 h-3 text-green-400 ml-1" />}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {!activeStoryboard ? (
              <div className="lg:col-span-2 text-center text-gray-400 text-sm py-20 bg-[#140a24]/60 border border-purple-500/10 rounded-2xl flex flex-col items-center gap-3">
                <Clapperboard className="w-10 h-10 text-purple-500/40" />
                Créez ou sélectionnez un storyboard pour commencer.
              </div>
            ) : (
              <>
                {/* Colonne centrale : titre + scènes + composer */}
                <div className="flex flex-col gap-4 min-w-0">
                  {/* Titre renommable */}
                  <div className="flex items-center gap-2">
                    {editingTitle ? (
                      <>
                        <input
                          value={titleDraft} onChange={e => setTitleDraft(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') saveTitle(); if (e.key === 'Escape') setEditingTitle(false); }}
                          autoFocus
                          className="flex-1 bg-black/30 border border-purple-400 rounded-xl px-3 py-2 text-white text-sm font-bold focus:outline-none"
                        />
                        <button onClick={saveTitle} disabled={!titleDraft.trim() || savingTitle} className="w-9 h-9 rounded-xl bg-purple-600 hover:bg-purple-500 disabled:opacity-40 flex items-center justify-center flex-shrink-0">
                          {savingTitle ? <Loader2 className="w-4 h-4 text-white animate-spin" /> : <Check className="w-4 h-4 text-white" />}
                        </button>
                        <button onClick={() => setEditingTitle(false)} className="w-9 h-9 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center flex-shrink-0">
                          <X className="w-4 h-4 text-white" />
                        </button>
                      </>
                    ) : (
                      <>
                        <h2 className="text-white font-black text-lg truncate">{activeStoryboard.title}</h2>
                        <button onClick={startEditTitle} className="w-7 h-7 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center flex-shrink-0 transition">
                          <Pencil className="w-3.5 h-3.5 text-gray-400" />
                        </button>
                      </>
                    )}
                  </div>

                  {/* Scènes, avec connecteurs "chaîne" entre elles */}
                  <div className="flex flex-col">
                    {scenes.map((scene, idx) => (
                      <React.Fragment key={scene.scene_id}>
                        {idx > 0 && (
                          <div className="flex items-center justify-center py-1">
                            <div className="w-0.5 h-6 bg-gradient-to-b from-purple-500/50 to-fuchsia-500/50" />
                          </div>
                        )}
                        <div className="relative bg-[#140a24]/95 border border-purple-500/15 rounded-2xl p-4 shadow-[0_4px_20px_rgba(0,0,0,0.3)]">
                          <div className="flex items-center gap-2.5 mb-2">
                            <div
                              className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-black flex-shrink-0"
                              style={{ background: 'linear-gradient(135deg, #9333EA, #DB2777)', boxShadow: '0 0 12px rgba(147,51,234,0.5)' }}
                            >
                              {idx + 1}
                            </div>
                            <span className="text-white font-bold text-xs flex-1">{providers[scene.provider]?.label || scene.provider}</span>
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                              scene.status === 'completed' ? 'bg-green-500/20 text-green-300'
                              : scene.status === 'failed' ? 'bg-red-500/20 text-red-300'
                              : 'bg-amber-500/20 text-amber-300'
                            }`}>
                              {scene.status === 'completed' ? 'Terminée' : scene.status === 'failed' ? 'Échouée' : 'En cours...'}
                            </span>
                            {(scene.status === 'completed' || scene.status === 'failed') && (
                              <button
                                onClick={() => startEditScene(scene)}
                                title="Modifier et régénérer cette scène"
                                className="flex-shrink-0 w-6 h-6 rounded-lg bg-white/5 hover:bg-amber-500/20 border border-white/10 hover:border-amber-400/40 flex items-center justify-center transition"
                              >
                                <Pencil className="w-3 h-3 text-gray-400 hover:text-amber-300" />
                              </button>
                            )}
                          </div>
                          <p className="text-gray-400 text-xs mb-2 line-clamp-2">{scene.prompt}</p>
                          {scene.status === 'queued' && <TeckiALoader subtitle="Génération de la scène..." />}
                          {scene.status === 'failed' && <div className="text-red-300 text-xs py-2">{scene.error}</div>}
                          {scene.status === 'completed' && scene.video_url && (
                            <video src={scene.video_url} controls className="w-full rounded-xl" />
                          )}
                        </div>
                      </React.Fragment>
                    ))}
                    {scenes.length > 0 && (
                      <div className="flex items-center justify-center py-1">
                        <ChevronDown className="w-5 h-5 text-purple-400/60" />
                      </div>
                    )}
                  </div>

                  {/* Nouvelle scène - composer complet, reutilise pour "Modifier" une scene existante */}
                  <div
                    id="scene-composer"
                    className="relative bg-[#140a24]/95 rounded-2xl p-4 overflow-hidden"
                    style={{
                      border: editingSceneId ? '1.5px dashed rgba(251,191,36,0.5)' : '1.5px dashed rgba(196,132,252,0.4)',
                      boxShadow: editingSceneId ? '0 0 30px rgba(251,191,36,0.12)' : '0 0 30px rgba(147,51,234,0.12)',
                    }}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-white font-bold text-sm flex items-center gap-1.5">
                        {editingSceneId ? (
                          <><Pencil className="w-4 h-4 text-amber-300" /> Modifier & régénérer la scène</>
                        ) : (
                          <><Sparkle className="w-4 h-4 text-fuchsia-300" /> Nouvelle scène</>
                        )}
                      </div>
                      {editingSceneId && (
                        <button onClick={cancelEditScene} className="text-[10px] text-gray-400 hover:text-white font-bold">
                          Annuler
                        </button>
                      )}
                    </div>
                    {editingSceneId && (
                      <p className="text-amber-200/80 text-[11px] mb-3">
                        Cette scène remplacera l'ancienne (même position). La nouvelle génération est facturée normalement, sans remboursement de l'ancienne.
                      </p>
                    )}
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      {providerTiles.map(({ key, cfg }) => {
                        const active = key === 'veo_group' ? isVeoTierKey(sceneProvider) : sceneProvider === key;
                        const label = key === 'veo_group' ? 'TeckiA V' : cfg.label;
                        const info = key === 'veo_group' ? VEO_GROUP_INFO : cfg.info;
                        const theme = providerTheme(key);
                        const Icon = theme.icon;
                        return (
                          <div key={key} className="relative group">
                            {key === 'seedance_mini' && (
                              <span className="absolute -top-2 right-1 z-10 px-1.5 py-0.5 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-[8px] font-bold shadow-[0_0_8px_rgba(16,185,129,0.6)]">
                                ⭐ Meilleur choix
                              </span>
                            )}
                            <button
                              onClick={() => onSceneProviderTileClick(key)}
                              className={`w-full flex items-center gap-1.5 px-2.5 py-2 rounded-lg text-[11px] font-bold border transition-all duration-300 ${
                                active
                                  ? `bg-gradient-to-br ${theme.color} border-white/40 text-white ${theme.glow}`
                                  : 'bg-white/5 border-white/10 text-gray-400 hover:border-white/25'
                              }`}
                            >
                              <div className={`flex-shrink-0 w-5 h-5 rounded-md flex items-center justify-center bg-gradient-to-br ${theme.color}`}>
                                <Icon className="w-3 h-3 text-white" />
                              </div>
                              <span className="truncate">{label}</span>
                            </button>
                            {info && (
                              <div className="pointer-events-none absolute z-20 left-0 right-0 top-full mt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-150">
                                <div className="bg-[#1c0f30] border border-purple-500/30 rounded-xl p-3 text-[11px] text-gray-300 leading-relaxed shadow-2xl">
                                  {info}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex gap-2 mb-3 bg-black/20 border border-white/10 rounded-xl p-1">
                      <button
                        onClick={() => setScenePromptMode('free')}
                        className={`flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-[11px] font-bold transition ${scenePromptMode === 'free' ? 'bg-purple-600 text-white' : 'text-gray-400 hover:text-gray-200'}`}
                      >
                        <MessageSquare className="w-3 h-3" /> Prompt libre
                      </button>
                      <button
                        onClick={() => setScenePromptMode('structured')}
                        className={`flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-[11px] font-bold transition ${scenePromptMode === 'structured' ? 'bg-purple-600 text-white' : 'text-gray-400 hover:text-gray-200'}`}
                      >
                        <SlidersHorizontal className="w-3 h-3" /> Champs détaillés
                      </button>
                    </div>

                    {scenePromptMode === 'free' ? (
                    <>
                    <div className="flex gap-2 mb-3">
                      <textarea
                        value={sceneBrief}
                        onChange={e => setSceneBrief(e.target.value)}
                        rows={2}
                        placeholder="Que voulez-vous pour cette scène ? (TeckiA rédige le prompt)"
                        className="flex-1 bg-black/30 border border-white/10 rounded-xl px-3 py-2.5 text-white text-xs placeholder-gray-500 focus:outline-none focus:border-purple-400 resize-y min-h-[76px]"
                      />
                      <button
                        onClick={generatePromptForScene}
                        disabled={!sceneBrief.trim() || generatingScenePrompt}
                        title="Générer le prompt"
                        className="flex-shrink-0 w-9 rounded-xl bg-purple-600 hover:bg-purple-500 disabled:opacity-40 flex items-center justify-center transition"
                      >
                        {generatingScenePrompt ? <Loader2 className="w-3.5 h-3.5 text-white animate-spin" /> : <MessageSquarePlus className="w-3.5 h-3.5 text-white" />}
                      </button>
                    </div>
                    <textarea
                      value={scenePrompt}
                      onChange={e => setScenePrompt(e.target.value)}
                      rows={2}
                      placeholder="Décrivez cette scène..."
                      className="w-full bg-black/30 border border-white/10 rounded-xl px-3 py-2.5 text-white text-xs placeholder-gray-500 focus:outline-none focus:border-purple-400 resize-y min-h-[76px] mb-3"
                    />
                    </>
                    ) : (
                    <div className="mb-3 space-y-2">
                      {STRUCTURED_FIELDS.map(f => {
                        const value = { environment: sceneFieldEnvironment, ambiance: sceneFieldAmbiance, character: sceneFieldCharacter, outfit: sceneFieldOutfit, movement: sceneFieldMovement, action: sceneFieldAction, info: sceneFieldInfo }[f.key];
                        const setter = sceneStructuredSetters[f.key];
                        return (
                          <div key={f.key}>
                            <label className="block text-gray-400 text-[10px] font-semibold mb-1">{f.label}</label>
                            <div className="flex items-center gap-1.5">
                              <input
                                value={value}
                                onChange={e => setter(e.target.value)}
                                onFocus={() => setSceneStructuredFocus(f.key)}
                                placeholder={f.placeholder}
                                className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-white text-xs placeholder-gray-500 focus:outline-none focus:border-purple-400"
                              />
                              <button
                                type="button"
                                onClick={() => openSceneFieldImagePicker(f.key)}
                                title={`Attacher une image à ${f.label}`}
                                disabled={sceneRefFiles.length >= (sceneCfg?.max_images || 7)}
                                className="flex-shrink-0 w-7 h-7 rounded-lg border border-white/10 bg-white/5 hover:bg-purple-600/30 hover:border-purple-400/50 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center text-gray-400 hover:text-purple-300 transition"
                              >
                                <Paperclip className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                      <input
                        type="file" accept="image/*" multiple ref={sceneFieldFileInputRef} className="hidden"
                        onChange={e => {
                          if (pendingSceneFieldKey) attachImageToSceneField(pendingSceneFieldKey, e.target.files);
                          e.target.value = '';
                          setPendingSceneFieldKey(null);
                        }}
                      />
                    </div>
                    )}
                    {sceneCfg && isVeoTierKey(sceneProvider) && (
                      <div className="mb-3">
                        <div className="text-[10px] text-gray-400 mb-1">Qualité TeckiA V</div>
                        <div className="flex gap-1">
                          {VEO_TIERS.filter(t => providers[t.key]).map(t => (
                            <button
                              key={t.key}
                              onClick={() => onSceneTierChange(t.key)}
                              className={`flex-1 px-2 py-1.5 rounded text-[10px] font-bold border transition ${
                                sceneProvider === t.key
                                  ? 'bg-purple-600 border-purple-400 text-white'
                                  : 'bg-white/5 border-white/10 text-gray-400'
                              }`}
                            >
                              {t.short}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {sceneCfg && (
                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <div>
                          <div className="text-[10px] text-gray-400 mb-1">Résolution</div>
                          <div className="flex flex-wrap gap-1">
                            {sceneCfg.resolutions.map(r => (
                              <button
                                key={r} onClick={() => {
                                  setSceneResolution(r);
                                  if (sceneCfg.video_ref_unsupported_resolutions?.includes(r)) {
                                    setSceneVideoFile(null); setSceneRefVideoFiles([]);
                                  }
                                }}
                                className={`px-2 py-1 rounded text-[10px] font-bold border transition ${sceneResolution === r ? 'bg-purple-600 border-purple-400 text-white' : 'bg-white/5 border-white/10 text-gray-400'}`}
                              >
                                {r}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] text-gray-400 mb-1">Durée : {sceneDuration}s</div>
                          {sceneProvider === 'omni' ? (
                            <p className="text-[10px] text-gray-500 mt-1">Durée fixe (même prix).</p>
                          ) : sceneCfg?.requires_audio ? (
                            <p className="text-[10px] text-gray-500 mt-1">Suit la durée de l'audio fourni.</p>
                          ) : sceneCfg?.requires_video ? (
                            <p className="text-[10px] text-gray-500 mt-1">Suit la durée de la vidéo fournie.</p>
                          ) : (
                            <input
                              type="range" min={1} max={sceneCfg.max_duration} value={sceneDuration}
                              onChange={e => setSceneDuration(parseInt(e.target.value, 10))}
                              className="w-full accent-purple-500"
                              disabled={!!sceneVideoFile}
                            />
                          )}
                        </div>
                      </div>
                    )}
                    {sceneCfg && sceneCfg.aspect_ratios.length > 1 && (
                      <div className="mb-3">
                        <div className="text-[10px] text-gray-400 mb-1">Format</div>
                        <div className="flex flex-wrap gap-1">
                          {sceneCfg.aspect_ratios.map(ar => (
                            <button
                              key={ar} onClick={() => setSceneAspectRatio(ar)}
                              className={`px-2 py-1 rounded text-[10px] font-bold border transition ${sceneAspectRatio === ar ? 'bg-purple-600 border-purple-400 text-white' : 'bg-white/5 border-white/10 text-gray-400'}`}
                            >
                              {ar}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {sceneCfg?.supports_generate_audio && (
                      <div className="mb-3 flex items-center justify-between bg-black/20 border border-white/10 rounded-xl px-3 py-2.5">
                        <div className="flex items-center gap-1.5">
                          <Music2 className="w-3.5 h-3.5 text-purple-300" />
                          <span className="text-white text-xs font-semibold">Générer l'audio</span>
                        </div>
                        <button
                          onClick={() => setSceneGenerateAudio(v => !v)}
                          className={`w-10 h-5.5 rounded-full transition relative flex-shrink-0 ${sceneGenerateAudio ? 'bg-purple-600' : 'bg-white/10'}`}
                          style={{ width: 40, height: 22 }}
                        >
                          <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${sceneGenerateAudio ? 'translate-x-5' : 'translate-x-0.5'}`} />
                        </button>
                      </div>
                    )}

                    {scenes.length > 0 && (
                      <label className="mb-3 flex items-center gap-2 bg-black/20 border border-white/10 rounded-lg px-3 py-2 cursor-pointer">
                        <input
                          type="checkbox" checked={sceneUseLastFrame}
                          onChange={e => setSceneUseLastFrame(e.target.checked)}
                          className="w-3.5 h-3.5 accent-purple-500"
                        />
                        <span className="text-[11px] text-gray-300">
                          Utiliser la dernière image de la scène précédente (continuité)
                        </span>
                      </label>
                    )}

                    <div className="mb-3">
                      <div className="text-[10px] text-gray-400 mb-1.5">
                        Photos de référence {sceneCfg?.requires_image && scenes.length === 0 ? '(requis pour la 1ère scène)' : `(optionnel, jusqu'à ${sceneCfg?.max_images || 7})`}
                      </div>
                      {scenes.length > 0 && sceneUseLastFrame && (
                        <p className="text-[10px] text-purple-300/80 mb-1.5">
                          {sceneRefFiles.length === 0
                            ? '↳ Continuité pure depuis la scène précédente.'
                            : '↳ Continuité + vos photos de référence ci-dessous seront combinées.'}
                        </p>
                      )}
                      <div className="flex flex-wrap gap-2">
                        {sceneRefPreviews.map((src, idx) => (
                          <button
                            key={idx} type="button" onClick={() => insertSceneImageTag(idx + 1)}
                            className="relative w-14 h-14 rounded-lg overflow-hidden border border-purple-500/25 hover:border-purple-400 transition"
                          >
                            <img src={src} alt="" className="w-full h-full object-cover" />
                            <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-center py-0.5">
                              <span className="text-[7px] font-bold text-purple-300">@image{idx + 1}</span>
                            </div>
                            <div
                              role="button"
                              onClick={e => { e.stopPropagation(); removeSceneRef(idx); }}
                              className="absolute top-0.5 right-0.5 w-4 h-4 bg-black/70 hover:bg-red-600 rounded-full flex items-center justify-center"
                            >
                              <X className="w-2.5 h-2.5 text-white" />
                            </div>
                          </button>
                        ))}
                        {sceneRefFiles.length < (sceneCfg?.max_images || 7) && (
                          <button
                            onClick={() => sceneFileInputRef.current?.click()}
                            className="w-14 h-14 rounded-lg border-2 border-dashed border-white/20 flex items-center justify-center text-gray-400 hover:text-purple-300 hover:border-purple-400/50 transition"
                          >
                            <Upload className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <input
                          ref={sceneFileInputRef} type="file" accept="image/*" multiple className="hidden"
                          onChange={e => { addSceneRefFiles(e.target.files, sceneCfg?.max_images || 7); e.target.value = ''; }}
                        />
                      </div>
                    </div>

                    {sceneCfg?.supports_video_ref && sceneCfg.max_videos === 1
                      && !sceneCfg.video_ref_unsupported_resolutions?.includes(sceneResolution) && (
                      <div className="mb-3">
                        <div className="text-[10px] text-gray-400 mb-1.5 flex items-center gap-1"><Video className="w-3 h-3 text-purple-300" /> Vidéo de référence {sceneCfg.requires_video ? `(requis, max ${sceneCfg.max_duration}s)` : '(optionnel)'}</div>
                        {sceneVideoPreview ? (
                          <div className="relative inline-block">
                            <video src={sceneVideoPreview} className="w-32 h-20 rounded-lg object-contain bg-black border border-purple-500/25" muted />
                            <button onClick={removeSceneVideo} className="absolute top-1 right-1 w-5 h-5 bg-black/70 hover:bg-red-600 rounded-full flex items-center justify-center">
                              <X className="w-3 h-3 text-white" />
                            </button>
                          </div>
                        ) : (
                          <button onClick={() => sceneVideoInputRef.current?.click()} className="w-32 h-20 rounded-lg border-2 border-dashed border-white/20 flex flex-col items-center justify-center text-gray-400 hover:text-purple-300 hover:border-purple-400/50 transition">
                            <Video className="w-4 h-4 mb-1" /><span className="text-[9px]">Ajouter</span>
                          </button>
                        )}
                        <input ref={sceneVideoInputRef} type="file" accept="video/*" className="hidden"
                          onChange={e => { onSceneVideoPicked(e.target.files?.[0]); e.target.value = ''; }} />
                      </div>
                    )}

                    {sceneCfg?.supports_first_last_frame && (
                      <div className="mb-3">
                        <div className="text-[10px] text-gray-400 mb-1.5 flex items-center gap-1"><ImagePlus className="w-3 h-3 text-purple-300" /> Première / dernière image</div>
                        <div className="flex gap-3">
                          {[{ key: 'first', label: 'Première', preview: sceneFirstFramePreview, ref: sceneFirstFrameInputRef },
                            { key: 'last', label: 'Dernière', preview: sceneLastFramePreview, ref: sceneLastFrameInputRef }].map(f => (
                            <div key={f.key}>
                              {f.preview ? (
                                <div className="relative w-14 h-14 rounded-lg overflow-hidden border border-purple-500/25">
                                  <img src={f.preview} alt="" className="w-full h-full object-cover" />
                                  <button onClick={() => removeSceneFrame(f.key)} className="absolute top-0.5 right-0.5 w-4 h-4 bg-black/70 hover:bg-red-600 rounded-full flex items-center justify-center">
                                    <X className="w-2.5 h-2.5 text-white" />
                                  </button>
                                </div>
                              ) : (
                                <button onClick={() => f.ref.current?.click()} className="w-14 h-14 rounded-lg border-2 border-dashed border-white/20 flex flex-col items-center justify-center text-gray-400 hover:text-purple-300 hover:border-purple-400/50 transition">
                                  <Upload className="w-3.5 h-3.5" /><span className="text-[8px] mt-0.5">{f.label}</span>
                                </button>
                              )}
                              <input ref={f.ref} type="file" accept="image/*" className="hidden"
                                onChange={e => { onSceneFramePicked(f.key, e.target.files?.[0]); e.target.value = ''; }} />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {sceneCfg?.supports_video_ref && sceneCfg.max_videos > 1 && (
                      <div className="mb-3">
                        <div className="text-[10px] text-gray-400 mb-1.5 flex items-center gap-1"><Video className="w-3 h-3 text-purple-300" /> Vidéos de référence (jusqu'à {sceneCfg.max_videos})</div>
                        <div className="flex flex-wrap gap-1.5">
                          {sceneRefVideoFiles.map((f, idx) => (
                            <div key={idx} className="flex items-center gap-1 bg-white/5 border border-white/10 rounded-lg px-2 py-1 text-[10px] text-gray-300">
                              <Video className="w-3 h-3 text-purple-300" /> {f.name.slice(0, 14)}
                              <button onClick={() => removeSceneRefVideo(idx)}><X className="w-2.5 h-2.5 text-gray-400 hover:text-red-400" /></button>
                            </div>
                          ))}
                          {sceneRefVideoFiles.length < sceneCfg.max_videos && (
                            <button onClick={() => sceneRefVideoInputRef.current?.click()} className="flex items-center gap-1 border-2 border-dashed border-white/20 rounded-lg px-2.5 py-1 text-[10px] text-gray-400 hover:text-purple-300 hover:border-purple-400/50 transition">
                              <Upload className="w-3 h-3" /> Ajouter
                            </button>
                          )}
                        </div>
                        <input ref={sceneRefVideoInputRef} type="file" accept="video/*" multiple className="hidden"
                          onChange={e => { addSceneRefVideoFiles(e.target.files, sceneCfg.max_videos); e.target.value = ''; }} />
                      </div>
                    )}

                    {sceneCfg?.supports_audio_ref && (
                      <div className="mb-3">
                        <div className="text-[10px] text-gray-400 mb-1.5 flex items-center gap-1"><Music2 className="w-3 h-3 text-purple-300" /> Audio{sceneCfg.max_audios > 1 ? 's de référence' : ''} {sceneCfg.requires_audio ? `(requis, max ${sceneCfg.max_duration}s)` : `(jusqu'à ${sceneCfg.max_audios})`}</div>
                        <div className="flex flex-wrap gap-1.5">
                          {sceneRefAudioFiles.map((f, idx) => (
                            <div key={idx} className="flex items-center gap-1 bg-white/5 border border-white/10 rounded-lg px-2 py-1 text-[10px] text-gray-300">
                              <Music2 className="w-3 h-3 text-purple-300" /> {f.name.slice(0, 14)}
                              <button onClick={() => removeSceneRefAudio(idx)}><X className="w-2.5 h-2.5 text-gray-400 hover:text-red-400" /></button>
                            </div>
                          ))}
                          {sceneRefAudioFiles.length < sceneCfg.max_audios && (
                            <button onClick={() => sceneRefAudioInputRef.current?.click()} className="flex items-center gap-1 border-2 border-dashed border-white/20 rounded-lg px-2.5 py-1 text-[10px] text-gray-400 hover:text-purple-300 hover:border-purple-400/50 transition">
                              <Upload className="w-3 h-3" /> Ajouter
                            </button>
                          )}
                        </div>
                        <input ref={sceneRefAudioInputRef} type="file" multiple className="hidden"
                          onChange={e => { addSceneRefAudioFiles(e.target.files, sceneCfg.max_audios); e.target.value = ''; }} />
                      </div>
                    )}

                    {sceneCfg && (
                      <div className={`mb-3 flex items-center justify-between rounded-xl px-3 py-2.5 border ${
                        scenePrice > credits ? 'bg-red-500/10 border-red-500/30' : 'bg-black/20 border-white/10'
                      }`}>
                        <span className="text-gray-400 text-[11px] flex items-center gap-1.5">
                          <Coins className="w-3.5 h-3.5" /> Coût de cette scène
                        </span>
                        <span className={`font-bold text-xs ${scenePrice > credits ? 'text-red-300' : 'text-amber-300'}`}>
                          {scenePrice.toLocaleString('fr-FR')} Ar
                        </span>
                      </div>
                    )}

                    <button
                      onClick={addScene}
                      disabled={!sceneEffectivePrompt.trim() || addingScene || (!editingSceneId && lastSceneQueued)}
                      className="w-full py-2.5 rounded-xl font-bold text-white text-sm flex items-center justify-center gap-2 disabled:opacity-40 transition hover:scale-[1.01]"
                      style={{ background: editingSceneId ? 'linear-gradient(135deg, #D97706, #DB2777)' : 'linear-gradient(135deg, #9333EA, #DB2777)' }}
                    >
                      {addingScene ? <Loader2 className="w-4 h-4 animate-spin" /> : editingSceneId ? <RefreshCw className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                      {editingSceneId ? 'Régénérer cette scène' : 'Ajouter la scène'}
                    </button>
                  </div>

                  <Retention14DaysNotice />
                </div>

                {/* Colonne droite : résultat assemblé, toujours visible */}
                <div className="lg:sticky lg:top-44">
                  <div className="relative bg-[#140a24]/95 border border-purple-500/25 rounded-2xl p-4 shadow-[0_0_40px_rgba(147,51,234,0.15)] overflow-hidden">
                    <div className="text-white font-bold text-sm mb-3 flex items-center gap-1.5">
                      <Clapperboard className="w-4 h-4 text-purple-300" /> Film assemblé
                    </div>
                    {activeStoryboard.final_video_url ? (
                      <>
                        <video src={activeStoryboard.final_video_url} controls className="w-full rounded-xl mb-3 shadow-[0_8px_24px_rgba(0,0,0,0.4)]" />
                        <a
                          href={activeStoryboard.final_video_url} download target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white text-xs font-semibold transition w-full justify-center"
                        >
                          <Download className="w-3.5 h-3.5" /> Télécharger le film
                        </a>
                      </>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-10 text-center gap-2">
                        <Clapperboard className="w-9 h-9 text-purple-500/30" />
                        <p className="text-gray-500 text-xs">Pas encore assemblé.<br />Ajoutez au moins 2 scènes terminées.</p>
                      </div>
                    )}
                    {completedScenesCount >= 2 && (
                      <button
                        onClick={assembleFilm}
                        disabled={assembling}
                        className="w-full mt-3 py-3 rounded-xl font-bold text-white flex items-center justify-center gap-2 disabled:opacity-40 transition hover:scale-[1.01]"
                        style={{ background: 'linear-gradient(135deg, #059669, #10B981)' }}
                      >
                        {assembling ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                        {assembling ? 'Assemblage en cours...' : activeStoryboard.final_video_url ? 'Réassembler (mettre à jour)' : 'Assembler le film'}
                      </button>
                    )}
                    <p className="text-[10px] text-gray-500 mt-2 text-center">Utilise toutes les {completedScenesCount} scène(s) terminée(s) depuis le début.</p>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {showTopup && (
        <TopupModal
          onClose={() => setShowTopup(false)}
          onSubmit={submitTopup}
          submitting={submittingTopup}
          serviceId="ia_studio_video"
          onCardSuccess={handleCardTopupSuccess}
        />
      )}

      {showMyStats && <MyConsumptionStatsModal onClose={() => setShowMyStats(false)} />}

      {trimModal && (
        <VideoTrimModal
          file={trimModal.file}
          maxDuration={trimModal.maxDuration}
          onConfirm={trimModal.onConfirm}
          onCancel={trimModal.onCancel}
        />
      )}
      {audioTrimModal && (
        <AudioTrimModal
          file={audioTrimModal.file}
          maxDuration={audioTrimModal.maxDuration}
          onConfirm={audioTrimModal.onConfirm}
          onCancel={audioTrimModal.onCancel}
        />
      )}
    </div>
  );
}
